import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays, parseISO, isSameDay } from 'date-fns';
import { getOpenPunchOutLabel } from '../utils/timecardOpenPunchLabel';
import {
  timecardApi,
  employeeApi,
  shiftApi,
  attendanceApi,
  scheduleApi,
  positionApi,
  breakTimeSettingsApi,
  leaveLedgerApi,
  locationApi,
  publicHolidayApi,
  employeePublicHolidayApi,
  companySettingsApi,
  invalidateEmployeeListReadCaches,
  invalidateWeekShiftAttendanceReadCaches,
} from '../services/api';
import { Timecard, TimecardDay, Employee, UpdateTimecardDayRequest, Shift, AttendanceRecord, Schedule, Position, BreakTimeSettings, LeaveLedger, Location, PublicHoliday, EmployeePublicHoliday } from '../types';
import { ChevronDown, ChevronUp, Calendar, Clock, Search, RefreshCw, MoreVertical, Trash2, X, Info, MapPin, FileText } from 'lucide-react';
import { WeekNavigator } from '../components/WeekNavigator';
import { useWeekStartDay } from '../hooks/useWeekStartDay';
import { useLocationContext } from '../components/Layout';
import { parseLocalDate } from '../utils/payrollPeriodLocal';
import InactiveLocationBanner from '../components/InactiveLocationBanner';
import { extractDatePart, extractTimezoneNaiveTime, formatDisplayTime, toSortableDateTime } from '../utils/datetime';
import { formatCompanyDate, formatCompanyDateRange } from '../utils/companyDateFormat';
import {
  calculateDefaultBreakMinutes,
  resolveShiftBreakIsPaid,
  resolveShiftBreakMinutes,
  shiftUsesGlobalBreakSettings,
} from '../utils/breakPolicy';

interface TimecardRow {
  date: Date;
  dateStr: string;
  shift?: Shift;
  attendanceRecord?: AttendanceRecord;
  dayData?: TimecardDay;
  shiftIndex: number; // 0 for first shift, 1 for second, etc.
  isUnscheduled?: boolean; // True if attendance without matching shift
  isManual?: boolean; // True if this is a manually added timecard row
  isLeave?: boolean; // True when row represents leave (from LeaveLedger)
  leaveHours?: number;
  leaveSourceType?: string; // AnnualLeave, PublicHoliday, UnpaidLeave
}

const Timecards: React.FC = () => {
  const weekStartsOn = useWeekStartDay(); // Universal week start day setting
  const { debouncedSelectedLocationId, selectedLocationId, locations, isSelectedLocationInactive, selectedLocation } = useLocationContext();
  
  // Load persisted week from localStorage
  const getInitialWeek = (): Date => {
    const savedWeek = localStorage.getItem('timecards_currentWeek');
    if (savedWeek) {
      try {
        const parsedDate = new Date(savedWeek);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate;
        }
      } catch (e) {
        console.error('Error parsing saved week:', e);
      }
    }
    return new Date();
  };
  
  // Load persisted employee ID from localStorage
  const getInitialEmployeeId = (): number | null => {
    const savedEmployeeId = localStorage.getItem('timecards_selectedEmployeeId');
    if (savedEmployeeId) {
      try {
        const parsedId = Number(savedEmployeeId);
        if (!isNaN(parsedId) && parsedId > 0) {
          return parsedId;
        }
      } catch (e) {
        console.error('Error parsing saved employee ID:', e);
      }
    }
    return null;
  };
  
  const [currentWeek, setCurrentWeek] = useState<Date>(getInitialWeek());
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(getInitialEmployeeId());
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [weekActivityEmployeeIds, setWeekActivityEmployeeIds] = useState<Set<number>>(() => new Set());
  const [timecard, setTimecard] = useState<Timecard | null>(null);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(false);
  const [referenceDataLoading, setReferenceDataLoading] = useState(false);
  const [weekTimecardsLoading, setWeekTimecardsLoading] = useState(false);
  const [wakeRefreshToken, setWakeRefreshToken] = useState(0);
  const lastReferenceFetchAtRef = useRef(0);
  const hiddenAtRef = useRef<number | null>(null);
  const [savingDays, setSavingDays] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [empFilter, setEmpFilter] = useState<'all' | 'approved' | 'unapproved'>('all');
  const [lastClickedField, setLastClickedField] = useState<Map<string, 'scheduledIn' | 'actualIn' | 'scheduledOut' | 'actualOut' | 'scheduledBreak' | 'actualBreak'>>(new Map());
  const [openMenuRow, setOpenMenuRow] = useState<string | null>(null); // Track which row's menu is open (row key: dateStr-shiftIndex)
  const [manualTimecardRows, setManualTimecardRows] = useState<Set<string>>(new Set()); // Track manual rows by dateStr-shiftIndex
  const [openScheduleDropdown, setOpenScheduleDropdown] = useState<string | null>(null); // Track open schedule dropdown by row key
  const [openPositionDropdown, setOpenPositionDropdown] = useState<string | null>(null); // Track open position dropdown by row key
  const [breakTimeSettings, setBreakTimeSettings] = useState<BreakTimeSettings | null>(null);
  const [debugSyncLoading, setDebugSyncLoading] = useState(false);
  const [showSyncStep1Modal, setShowSyncStep1Modal] = useState(false);
  const [syncStep1Data, setSyncStep1Data] = useState<Array<{ shiftId: number; date: string; inTime: string; outTime: string; breakMins: number | null; breakPaid: boolean; breakUseGlobalSettings?: boolean }>>([]);
  const [syncStep2Data, setSyncStep2Data] = useState<Array<{ attendanceId: number; date: string; inTime: string; outTime: string | null; adjustedInTime: string | null; adjustedOutTime: string | null }>>([]);
  const [syncStep3Data, setSyncStep3Data] = useState<Array<{ attendanceId: number; date: string; inTime: string; outTime: string | null }>>([]);
  const [syncStep4Data, setSyncStep4Data] = useState<Array<{ shiftId: number; shiftDate: string; shiftInTime: string; shiftOutTime: string; shiftBreakMins: number; shiftBreakPaid: boolean; actualInTime: string | null; actualOutTime: string | null; actualInDate: string; actualOutDate: string; actualBreakMins: number | null; matchedAttendanceCount: number }>>([]);
  const [syncStep5Data, setSyncStep5Data] = useState<Array<{ shiftId: number | null; shiftDate: string | null; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualInDate: string; actualOutDate: string; actualBreakMins: number | null; matchedAttendanceCount: number }>>([]);
  const [syncStep7Data, setSyncStep7Data] = useState<Array<{ date: string; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualBreakMins: number | null; hash: string }>>([]);
  const [syncStep8Data, setSyncStep8Data] = useState<Array<{ date: string; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualBreakMins: number | null; hash: string; matchType: string }>>([]);
  const [syncStep9Data, setSyncStep9Data] = useState<Array<{ date: string; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualBreakMins: number | null; hash: string; matchType: string; paidInTime: string | null; paidOutTime: string | null; paidBreakMins: number | null; paidBreakPaid: boolean | null }>>([]);
  const [syncStep10Data, setSyncStep10Data] = useState<Array<{ date: string; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualBreakMins: number | null; hash: string; matchType: string; paidInTime: string | null; paidOutTime: string | null; paidBreakMins: number | null; paidBreakPaid: boolean | null; totalHours: number | null; status: string }>>([]);
  const employeeListRef = useRef<HTMLDivElement>(null);
  const selectedEmployeeRef = useRef<HTMLButtonElement>(null);
  const hasScrolledToInitialEmployee = useRef(false);
  const isSyncingRef = useRef(false); // Track if sync is in progress to prevent concurrent syncs
  const [showApprovedBlockMessage, setShowApprovedBlockMessage] = useState(false);
  const [showUnapproveConfirm, setShowUnapproveConfirm] = useState(false);
  const [unapproveError, setUnapproveError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ dateStr: string; shiftIndex: number; dayDataId?: number; dateLabel: string } | null>(null);
  const [showHardResyncConfirm, setShowHardResyncConfirm] = useState(false);
  const [hardResyncLoading, setHardResyncLoading] = useState(false);
  const [hardResyncError, setHardResyncError] = useState<string | null>(null);
  const [syncAllInProgress, setSyncAllInProgress] = useState(false);
  const [syncAllProgress, setSyncAllProgress] = useState(0);
  const [syncAllStatus, setSyncAllStatus] = useState('');
  const [overlapError, setOverlapError] = useState<string | null>(null);
  const [otherLocationsFromApi, setOtherLocationsFromApi] = useState<Array<{ locationId: number; locationName: string; totalHours: number }>>([]);
  const [leaveEntries, setLeaveEntries] = useState<LeaveLedger[]>([]);
  const [publicHolidaysForWeek, setPublicHolidaysForWeek] = useState<PublicHoliday[]>([]);
  const [employeePublicHolidays, setEmployeePublicHolidays] = useState<EmployeePublicHoliday[]>([]);
  const [allTimecardsForWeek, setAllTimecardsForWeek] = useState<Timecard[]>([]);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [companyCurrency, setCompanyCurrency] = useState<string>('EUR €');
  const [weeklyPremiumHours, setWeeklyPremiumHours] = useState<{ sundayPremiumHours: number; bhPremiumHours: number }>({
    sundayPremiumHours: 0,
    bhPremiumHours: 0,
  });

  useEffect(() => {
    let cancelled = false;
    companySettingsApi
      .get()
      .then((s) => {
        if (!cancelled && s?.currency) setCompanyCurrency(s.currency);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Calculate week start using universal week start day setting
  const weekStart = useMemo(() => startOfWeek(currentWeek, { weekStartsOn }), [currentWeek, weekStartsOn]);
  const weekEnd = useMemo(() => endOfWeek(currentWeek, { weekStartsOn }), [currentWeek, weekStartsOn]);

  // Generate array of dates for the week
  const weekDates = useMemo(() => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      dates.push(addDays(weekStart, i));
    }
    return dates;
  }, [weekStart]);

  const weekDateStrSet = useMemo(
    () => new Set(weekDates.map((d) => format(d, 'yyyy-MM-dd'))),
    [weekDates]
  );

  // Sync can attach attendance from adjacent days; totals should match the visible week grid only.
  const sumHoursInCurrentWeek = useCallback(
    (days: TimecardDay[] | undefined | null): number => {
      if (!days?.length) return 0;
      return days.reduce((sum, day) => {
        if (!day.date) return sum;
        let dateStr = day.date;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          try {
            dateStr = format(parseISO(dateStr), 'yyyy-MM-dd');
          } catch {
            return sum;
          }
        }
        return weekDateStrSet.has(dateStr) ? sum + (day.totalHours || 0) : sum;
      }, 0);
    },
    [weekDateStrSet]
  );

  // Persist current week to localStorage
  useEffect(() => {
    localStorage.setItem('timecards_currentWeek', currentWeek.toISOString());
  }, [currentWeek]);

  // Persist selected employee ID to localStorage
  useEffect(() => {
    if (selectedEmployeeId !== null) {
      localStorage.setItem('timecards_selectedEmployeeId', selectedEmployeeId.toString());
    } else {
      localStorage.removeItem('timecards_selectedEmployeeId');
    }
  }, [selectedEmployeeId]);

  const PAGE_DATA_STALE_MS = 2 * 60 * 1000;

  const loadReferenceData = useCallback(async (options?: { bustCaches?: boolean }) => {
    if (options?.bustCaches) {
      invalidateEmployeeListReadCaches();
    }
    setReferenceDataLoading(true);
    try {
      const [employeesData, schedulesData, positionsData, breakTimeData] = await Promise.all([
        employeeApi.getPickerAsEmployees(),
        scheduleApi.getAll(),
        positionApi.getAll(),
        breakTimeSettingsApi.get().catch(() => ({ id: undefined, isPaid: false, rules: [] })),
      ]);
      setAllEmployees(employeesData);
      setSchedules(schedulesData);
      setPositions(positionsData);
      setBreakTimeSettings(breakTimeData || { id: undefined, isPaid: false, rules: [] });
      lastReferenceFetchAtRef.current = Date.now();
    } catch (error) {
      console.error('Error fetching timecards reference data:', error);
    } finally {
      setReferenceDataLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReferenceData();
  }, [loadReferenceData]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      if (document.visibilityState !== 'visible') return;

      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      const hiddenDuration = hiddenAt != null ? Date.now() - hiddenAt : 0;
      const referenceStale =
        lastReferenceFetchAtRef.current === 0 ||
        Date.now() - lastReferenceFetchAtRef.current > PAGE_DATA_STALE_MS;

      if (hiddenDuration >= PAGE_DATA_STALE_MS || referenceStale) {
        invalidateWeekShiftAttendanceReadCaches();
        void loadReferenceData({ bustCaches: true });
        setWakeRefreshToken((t) => t + 1);
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [loadReferenceData, PAGE_DATA_STALE_MS]);

  // Fetch week attendance/shifts to surface inactive employees with activity
  const refreshWeekActivityEmployeeIds = useCallback(async () => {
    if (!debouncedSelectedLocationId || debouncedSelectedLocationId === 0) {
      setWeekActivityEmployeeIds(new Set());
      return;
    }
    const weekStartStr = format(weekStart, 'yyyy-MM-dd');
    const weekEndStr = format(weekEnd, 'yyyy-MM-dd');
    try {
      const [attendanceData, shiftsData] = await Promise.all([
        attendanceApi.getAll({
          startDate: weekStartStr,
          endDate: weekEndStr,
          locationId: debouncedSelectedLocationId,
        }),
        shiftApi.getAll({
          startDate: weekStartStr,
          endDate: weekEndStr,
          locationId: debouncedSelectedLocationId,
        }),
      ]);
      const ids = new Set<number>();
      attendanceData.forEach((record) => {
        if (record.employeeId) ids.add(record.employeeId);
      });
      shiftsData.forEach((shift) => {
        if (shift.employeeId) ids.add(shift.employeeId);
      });
      setWeekActivityEmployeeIds(ids);
    } catch (error) {
      console.error('Error fetching week activity for timecards list:', error);
      setWeekActivityEmployeeIds(new Set());
    }
  }, [debouncedSelectedLocationId, weekStart, weekEnd]);

  useEffect(() => {
    void refreshWeekActivityEmployeeIds();
  }, [refreshWeekActivityEmployeeIds, wakeRefreshToken]);

  // Active employees always; inactive only when they have timecard hours or week attendance/shifts
  const employees = useMemo(() => {
    if (!allEmployees.length) return [];
    return allEmployees.filter((emp) => {
      if (emp.isActive) return true;
      const cachedTimecard = allTimecardsForWeek.find((tc) => tc.employeeId === emp.id);
      const workHrs = sumHoursInCurrentWeek(cachedTimecard?.days);
      if (workHrs > 0) return true;
      return weekActivityEmployeeIds.has(emp.id);
    });
  }, [allEmployees, allTimecardsForWeek, weekActivityEmployeeIds, sumHoursInCurrentWeek]);

  const employeeMatchesSelectedLocation = useCallback(
    (employee: Employee) => {
      if (debouncedSelectedLocationId === null || debouncedSelectedLocationId === 0) return true;
      const hasMainLocation = employee.profile?.mainLocationId === debouncedSelectedLocationId;
      const hasSubLocation =
        employee.profile?.subLocationIds &&
        Array.isArray(employee.profile.subLocationIds) &&
        employee.profile.subLocationIds.includes(debouncedSelectedLocationId);
      if (hasMainLocation || hasSubLocation) return true;
      if (!employee.isActive && weekActivityEmployeeIds.has(employee.id)) return true;
      return false;
    },
    [debouncedSelectedLocationId, weekActivityEmployeeIds]
  );

  // Fetch shifts for the employee and week
  useEffect(() => {
    if (!selectedEmployeeId || !debouncedSelectedLocationId || debouncedSelectedLocationId === 0) {
      setShifts([]);
      return;
    }

    const fetchShifts = async () => {
      try {
        const weekStartStr = format(weekStart, 'yyyy-MM-dd');
        const weekEndStr = format(weekEnd, 'yyyy-MM-dd');
        const data = await shiftApi.getAll({
          employeeId: selectedEmployeeId,
          startDate: weekStartStr,
          endDate: weekEndStr,
          locationId: debouncedSelectedLocationId || undefined
        });
        setShifts(data);
      } catch (error) {
        console.error('Error fetching shifts:', error);
        setShifts([]);
      }
    };

    fetchShifts();
  }, [selectedEmployeeId, debouncedSelectedLocationId, weekStart, weekEnd]);

  // Fetch attendance records for the employee and week
  useEffect(() => {
    if (!selectedEmployeeId || !debouncedSelectedLocationId || debouncedSelectedLocationId === 0) {
      setAttendanceRecords([]);
      return;
    }

    const fetchAttendance = async () => {
      try {
        const weekStartStr = format(weekStart, 'yyyy-MM-dd');
        const weekEndStr = format(weekEnd, 'yyyy-MM-dd');
        const data = await attendanceApi.getAll({
          employeeId: selectedEmployeeId,
          startDate: weekStartStr,
          endDate: weekEndStr,
          locationId: debouncedSelectedLocationId || undefined
        });
        setAttendanceRecords(data);
      } catch (error) {
        console.error('Error fetching attendance records:', error);
        setAttendanceRecords([]);
      }
    };

    fetchAttendance();
  }, [selectedEmployeeId, debouncedSelectedLocationId, weekStart, weekEnd]);

  const runSelectedEmployeeSyncIfNeeded = useCallback(async () => {
    if (!selectedEmployeeId || !debouncedSelectedLocationId || debouncedSelectedLocationId === 0) return;
    if (isSyncingRef.current) return;

    const weekStartStr = format(weekStart, 'yyyy-MM-dd');
    try {
      isSyncingRef.current = true;
      const syncCheck = await timecardApi.checkSyncNeeded(
        selectedEmployeeId,
        weekStartStr,
        debouncedSelectedLocationId ?? undefined,
      );
      if (!syncCheck.syncNeeded) return;

      await timecardApi.sync(
        selectedEmployeeId,
        weekStartStr,
        debouncedSelectedLocationId ?? undefined,
      );
      const refreshed = await timecardApi.getByEmployeeAndWeek(
        selectedEmployeeId,
        weekStartStr,
        debouncedSelectedLocationId ?? undefined,
      );
      if (!refreshed) return;

      const normalized = { ...refreshed, days: refreshed.days ?? [] };
      setTimecard(normalized);
      setAllTimecardsForWeek((prev) => {
        const index = prev.findIndex((tc) => tc.employeeId === selectedEmployeeId);
        if (index >= 0) {
          const next = [...prev];
          next[index] = normalized;
          return next;
        }
        return [...prev, normalized];
      });
    } catch (syncError: any) {
      console.error(
        '[Timecard] Sync error:',
        syncError?.response?.data?.message || syncError?.message,
        syncError?.response?.data,
      );
    } finally {
      isSyncingRef.current = false;
    }
  }, [selectedEmployeeId, debouncedSelectedLocationId, weekStart]);

  // Fetch timecard when employee or week changes - load DB state first, sync in background
  useEffect(() => {
    if (!selectedEmployeeId || !debouncedSelectedLocationId || debouncedSelectedLocationId === 0) {
      setTimecard(null);
      return;
    }

    let cancelled = false;

    const fetchTimecard = async () => {
      setLoading(true);
      try {
        const weekStartStr = format(weekStart, 'yyyy-MM-dd');
        const weekEndStr = format(weekEnd, 'yyyy-MM-dd');

        const shiftsData = await shiftApi.getAll({
          employeeId: selectedEmployeeId,
          startDate: weekStartStr,
          endDate: weekEndStr,
          locationId: debouncedSelectedLocationId || undefined,
        });
        if (cancelled) return;
        setShifts(shiftsData);

        const attendanceData = await attendanceApi.getAll({
          employeeId: selectedEmployeeId,
          startDate: weekStartStr,
          endDate: weekEndStr,
          locationId: debouncedSelectedLocationId || undefined,
        });
        if (cancelled) return;
        setAttendanceRecords(attendanceData);

        const data = await timecardApi.getByEmployeeAndWeek(
          selectedEmployeeId,
          weekStartStr,
          debouncedSelectedLocationId ?? undefined,
        );
        if (cancelled) return;

        const resolvedTimecard = data
          ? { ...data, days: data.days ?? [] }
          : {
          id: 0,
          employeeId: selectedEmployeeId,
          weekStartDate: weekStartStr,
          createdAt: new Date().toISOString(),
          days: weekDates.map((date) => ({
            id: 0,
            date: format(date, 'yyyy-MM-dd'),
            mode: 'Auto' as const,
            extras: 0,
            totalHours: 0,
          })),
        };
        setTimecard(resolvedTimecard);
        setAllTimecardsForWeek((prev) => {
          const index = prev.findIndex((tc) => tc.employeeId === selectedEmployeeId);
          if (index >= 0) {
            const next = [...prev];
            next[index] = resolvedTimecard;
            return next;
          }
          return [...prev, resolvedTimecard];
        });
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching timecard:', error);
          setTimecard(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }

      if (!cancelled) {
        void runSelectedEmployeeSyncIfNeeded();
      }
    };

    void fetchTimecard();
    return () => {
      cancelled = true;
    };
  }, [
    selectedEmployeeId,
    debouncedSelectedLocationId,
    weekStart,
    weekDates,
    weekEnd,
    wakeRefreshToken,
    runSelectedEmployeeSyncIfNeeded,
  ]);

  const loadWeekTimecards = useCallback(async () => {
    if (!debouncedSelectedLocationId || debouncedSelectedLocationId === 0) {
      setAllTimecardsForWeek([]);
      setWeekTimecardsLoading(false);
      return;
    }

    setWeekTimecardsLoading(true);
    try {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      const data = await timecardApi.getAll(undefined, weekStartStr, debouncedSelectedLocationId);
      setAllTimecardsForWeek(data);
    } catch (error) {
      console.error('Error fetching week timecards for sidebar:', error);
    } finally {
      setWeekTimecardsLoading(false);
    }
  }, [debouncedSelectedLocationId, weekStart]);

  // Fetch all timecards for the week (for sidebar total hours)
  useEffect(() => {
    void loadWeekTimecards();
  }, [loadWeekTimecards, wakeRefreshToken]);

  // Sync notes draft when timecard changes
  useEffect(() => {
    if (timecard) {
      setNotesDraft(timecard.notes ?? '');
    }
  }, [timecard?.id, timecard?.notes]);

  // Sunday / BH premium hours for the selected employee week (payroll-aligned)
  useEffect(() => {
    if (!selectedEmployeeId) {
      setWeeklyPremiumHours({ sundayPremiumHours: 0, bhPremiumHours: 0 });
      return;
    }
    const weekStartStr = format(weekStart, 'yyyy-MM-dd');
    timecardApi
      .getWeeklyPremiumHours(selectedEmployeeId, weekStartStr)
      .then(setWeeklyPremiumHours)
      .catch(() => setWeeklyPremiumHours({ sundayPremiumHours: 0, bhPremiumHours: 0 }));
  }, [selectedEmployeeId, weekStart, timecard?.updatedAt, timecard?.id]);

  // Fetch OTHER location totals only (selected location comes from timecard - no API call for it)
  useEffect(() => {
    if (!selectedEmployeeId || !debouncedSelectedLocationId) {
      setOtherLocationsFromApi([]);
      return;
    }
    const fetchOtherLocationTotals = async () => {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      console.log('[LocationTotals] Fetching other locations: employeeId=', selectedEmployeeId, 'excludeLocationId=', debouncedSelectedLocationId, 'weekStart=', weekStartStr);
      const totals = await timecardApi.getLocationTotalsForEmployeeAndWeek(selectedEmployeeId, weekStartStr, debouncedSelectedLocationId);
      const filtered = totals.filter(t => t.totalHours > 0).map(t => ({ locationId: t.locationId ?? 0, locationName: t.locationName, totalHours: t.totalHours }));
      console.log('[LocationTotals] API returned', totals.length, '-> filtered to', filtered.length, filtered);
      setOtherLocationsFromApi(filtered);
    };
    fetchOtherLocationTotals();
  }, [selectedEmployeeId, debouncedSelectedLocationId, weekStart]);

  // Merge selected location (from timecard) + other locations (from API)
  const otherLocationTotals = useMemo(() => {
    const results: Array<{ locationId: number; locationName: string; totalHours: number; isSelected?: boolean }> = [];
    const selectedTotal = sumHoursInCurrentWeek(timecard?.days);
    const selectedLoc = locations?.find(l => l.id === debouncedSelectedLocationId);
    if (selectedLoc) {
      results.push({ locationId: selectedLoc.id, locationName: selectedLoc.name, totalHours: selectedTotal, isSelected: true });
    }
    results.push(...otherLocationsFromApi);
    return results;
  }, [timecard, locations, debouncedSelectedLocationId, otherLocationsFromApi, sumHoursInCurrentWeek]);

  // Fetch leave ledger for employee and week
  useEffect(() => {
    if (!selectedEmployeeId) {
      setLeaveEntries([]);
      return;
    }
    setLeaveEntries([]);
    const fetchLeave = async () => {
      try {
        const weekStartStr = format(weekStart, 'yyyy-MM-dd');
        const weekEndStr = format(weekEnd, 'yyyy-MM-dd');
        const data = await leaveLedgerApi.getByEmployeeAndDateRange(selectedEmployeeId, weekStartStr, weekEndStr);
        setLeaveEntries(data);
      } catch {
        setLeaveEntries([]);
      }
    };
    fetchLeave();
  }, [selectedEmployeeId, weekStart, weekEnd]);

  // Public holidays + per-employee eligibility (PD-024) — hints on days with no paid PH entitlement
  useEffect(() => {
    if (!selectedEmployeeId) {
      setPublicHolidaysForWeek([]);
      setEmployeePublicHolidays([]);
      return;
    }
    const load = async () => {
      const y1 = weekStart.getFullYear();
      const y2 = weekEnd.getFullYear();
      try {
        const [hy1, hy2, eph] = await Promise.all([
          publicHolidayApi.getAll(y1).catch(() => [] as PublicHoliday[]),
          y2 !== y1 ? publicHolidayApi.getAll(y2).catch(() => [] as PublicHoliday[]) : Promise.resolve([] as PublicHoliday[]),
          employeePublicHolidayApi.getAll(selectedEmployeeId).catch(() => [] as EmployeePublicHoliday[]),
        ]);
        setPublicHolidaysForWeek(y2 !== y1 ? [...hy1, ...hy2] : hy1);
        setEmployeePublicHolidays(eph);
      } catch {
        setPublicHolidaysForWeek([]);
        setEmployeePublicHolidays([]);
      }
    };
    load();
  }, [selectedEmployeeId, weekStart, weekEnd]);

  const phNotEligibleByDateStr = useMemo(() => {
    const map = new Map<string, string>();
    const weekStartStr = format(weekStart, 'yyyy-MM-dd');
    const weekEndStr = format(weekEnd, 'yyyy-MM-dd');
    for (const h of publicHolidaysForWeek) {
      if (h.isActive === false) continue;
      const d = (h.holidayDate || h.date || '').replace(/T.*/, '');
      if (!d || d < weekStartStr || d > weekEndStr) continue;
      const notEl = employeePublicHolidays.some((e) => e.holidayId === h.id && e.eligible === false);
      if (notEl) map.set(d, h.holidayName || h.name || 'Public holiday');
    }
    return map;
  }, [publicHolidaysForWeek, employeePublicHolidays, weekStart, weekEnd]);

  const renderDayColumnLabel = (row: TimecardRow, show: boolean) => {
    if (!show) return null;
    const phNotEligible = phNotEligibleByDateStr.has(row.dateStr);
    return (
      <div className="flex flex-col gap-0.5">
        <span>{format(row.date, 'EEEE')} {formatCompanyDate(row.date)}</span>
        {phNotEligible && (
          <span
            className="text-xs font-normal text-amber-800 max-w-[11rem] leading-tight"
            title="Paid public-holiday leave and bank-holiday premiums do not apply for this employee on this date (PD-024)."
          >
            PH — not eligible
          </span>
        )}
      </div>
    );
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuRow !== null) {
        setOpenMenuRow(null);
      }
      if (openScheduleDropdown !== null) {
        setOpenScheduleDropdown(null);
      }
      if (openPositionDropdown !== null) {
        setOpenPositionDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openMenuRow, openScheduleDropdown, openPositionDropdown]);

  // Helper function to check if a shift and attendance record overlap
  const doesShiftOverlapAttendance = (shift: Shift, attendance: AttendanceRecord): boolean => {
    const shiftStart = parseISO(shift.startTime);
    const shiftEnd = parseISO(shift.endTime);
    const clockIn = parseISO(attendance.clockInTime);
    const clockOut = attendance.clockOutTime ? parseISO(attendance.clockOutTime) : null;

    // Check overlap conditions:
    // 1. Scheduled IN between ClockIn AND ClockOut OR
    // 2. ScheduledOut between ClockIn and ClockOut OR
    // 3. ClockIn between ScheduledIN and ScheduledOut OR
    // 4. ClockOut between ScheduledIN and ScheduledOut
    
    if (clockOut) {
      // Both times exist - check for any overlap
      // Shift starts during attendance OR attendance starts during shift
      return (shiftStart >= clockIn && shiftStart <= clockOut) ||
             (shiftEnd >= clockIn && shiftEnd <= clockOut) ||
             (clockIn >= shiftStart && clockIn <= shiftEnd) ||
             (clockOut >= shiftStart && clockOut <= shiftEnd);
    } else {
      // No clock out - check if shift overlaps with clock in time
      return (shiftStart <= clockIn && shiftEnd >= clockIn) ||
             (clockIn >= shiftStart && clockIn <= shiftEnd);
    }
  };

  const extractTime = formatDisplayTime;

  const groupAttendanceBySchedule = (records: AttendanceRecord[]): AttendanceRecord[][] => {
    const groups = new Map<string, AttendanceRecord[]>();
    records.forEach(record => {
      const key = record.scheduleId != null ? String(record.scheduleId) : 'none';
      const existing = groups.get(key) ?? [];
      existing.push(record);
      groups.set(key, existing);
    });
    return Array.from(groups.values());
  };

  const buildCombinedAttendanceRow = (records: AttendanceRecord[]): AttendanceRecord => {
    const sorted = [...records].sort((a, b) =>
      extractTimezoneNaiveTime(a.clockInTime).localeCompare(extractTimezoneNaiveTime(b.clockInTime))
    );
    const lastClockOut = sorted
      .filter(a => a.clockOutTime)
      .sort((a, b) =>
        extractTimezoneNaiveTime(b.clockOutTime!).localeCompare(extractTimezoneNaiveTime(a.clockOutTime!))
      )[0]?.clockOutTime;

    return {
      ...sorted[0],
      id: sorted[0].id,
      clockInTime: sorted[0].clockInTime,
      clockOutTime: lastClockOut || undefined,
      breakMinutes: sorted.reduce((sum, record) => sum + (record.breakMinutes || 0), 0),
    };
  };

  type TimeWindow = { start: string; end: string };

  const dateTimesOverlap = (a: TimeWindow, b: TimeWindow): boolean =>
    a.start < b.end && a.end > b.start;

  const getAttendanceTimeWindow = (record: AttendanceRecord): TimeWindow | null => {
    const start = toSortableDateTime(record.clockInTime);
    const end = record.clockOutTime ? toSortableDateTime(record.clockOutTime) : '';
    if (!start || !end) return null;
    return { start, end };
  };

  const getTimecardDayTimeWindow = (dayData: TimecardDay): TimeWindow | null => {
    const inVal = dayData.actualIn ?? dayData.scheduledIn;
    const outVal = dayData.actualOut ?? dayData.scheduledOut;
    if (!inVal || !outVal) return null;
    const start = toSortableDateTime(inVal);
    const end = toSortableDateTime(outVal);
    if (!start || !end) return null;
    return { start, end };
  };

  const getShiftTimeWindow = (shift: Shift): TimeWindow | null => {
    const start = toSortableDateTime(shift.startTime);
    const end = toSortableDateTime(shift.endTime);
    if (!start || !end) return null;
    return { start, end };
  };

  const getTableRowTimeWindow = (
    row: Pick<TimecardRow, 'dayData' | 'shift' | 'attendanceRecord'>
  ): TimeWindow | null => {
    if (row.dayData) {
      return getTimecardDayTimeWindow(row.dayData);
    }
    if (row.attendanceRecord) {
      return getAttendanceTimeWindow(row.attendanceRecord);
    }
    if (row.shift) {
      return getShiftTimeWindow(row.shift);
    }
    return null;
  };

  /** Combine only when merged attendance does not overlap other rows on the day. */
  const shouldCombineAttendanceGroup = (
    group: AttendanceRecord[],
    otherWindows: TimeWindow[]
  ): boolean => {
    if (group.length <= 1) return true;
    const combinedWindow = getAttendanceTimeWindow(buildCombinedAttendanceRow(group));
    if (!combinedWindow) return false;
    return !otherWindows.some(window => dateTimesOverlap(combinedWindow, window));
  };

  const attendanceRecordsForUnscheduledGroup = (
    group: AttendanceRecord[],
    otherWindows: TimeWindow[]
  ): AttendanceRecord[] =>
    shouldCombineAttendanceGroup(group, otherWindows)
      ? [buildCombinedAttendanceRow(group)]
      : group;

  /** True when attendance belongs to a synced combined row (same schedule, clock-in within row window). */
  const attendanceCoveredBySyncedDay = (dayData: TimecardDay, attendance: AttendanceRecord): boolean => {
    if (
      dayData.scheduleId != null &&
      attendance.scheduleId != null &&
      dayData.scheduleId !== attendance.scheduleId
    ) {
      return false;
    }
    if (!dayData.actualIn) {
      return false;
    }

    const attSortable = toSortableDateTime(attendance.clockInTime);
    const rowInSortable = toSortableDateTime(dayData.actualIn);
    if (!dayData.actualOut) {
      return attSortable === rowInSortable;
    }

    const rowOutSortable = toSortableDateTime(dayData.actualOut);
    return attSortable >= rowInSortable && attSortable <= rowOutSortable;
  };

  const attendanceMatchesSyncedDayTimes = (
    attendance: AttendanceRecord,
    dayDataRecords: TimecardDay[]
  ): boolean =>
    dayDataRecords.some(dayData => {
      if (!dayData.actualIn || !dayData.actualOut || !attendance.clockOutTime) return false;
      return (
        toSortableDateTime(dayData.actualIn) === toSortableDateTime(attendance.clockInTime) &&
        toSortableDateTime(dayData.actualOut) === toSortableDateTime(attendance.clockOutTime)
      );
    });

  // Build rows for the table - build from timecard days (DB records) if they exist, otherwise from shifts/attendance
  const tableRows = useMemo(() => {
    const rows: TimecardRow[] = [];
    
    weekDates.forEach(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      
      // Get all timecard day records for this date (can be multiple now)
      // Use date comparison that is robust to both "yyyy-MM-dd" and full DateTime strings.
      const dayDataRecords =
        timecard?.days?.filter(d => {
          if (!d.date) return false;
          try {
            // If it's already a plain date string, compare directly first
            if (/^\d{4}-\d{2}-\d{2}$/.test(d.date)) {
              return d.date === dateStr;
            }
            // Fallback: parse as Date/DateTime and compare by day
            const dayDate = parseISO(d.date);
            return !isNaN(dayDate.getTime()) && isSameDay(dayDate, date);
          } catch {
            return false;
          }
        }) || [];
      
      // Get shifts for this date
      const dayShifts = shifts.filter(shift => extractDatePart(shift.startTime) === dateStr);

      // Get attendance records for this date (timezone-naive — matches Attendance page)
      const dayAttendance = attendanceRecords.filter(
        attendance => extractDatePart(attendance.clockInTime) === dateStr
      );

      // If we have timecard day records, build rows from them (mirror DB),
      // but when a specific location is selected, only show actual times that
      // have matching attendance in that location.
      if (dayDataRecords.length > 0) {
        const matchedAttendanceIds = new Set<number>();

        dayDataRecords.forEach((dayData, index) => {
          // Try to find matching shift based on scheduled times
          let matchedShift: Shift | undefined;
          if (dayData.scheduledIn) {
            // Find shift that matches scheduled times (compare time portion)
            // Extract time from DateTime string if needed
            const scheduledInTime = extractTime(dayData.scheduledIn);
            matchedShift = dayShifts.find(shift => {
              const shiftDate = parseISO(shift.startTime);
              const shiftTime = format(shiftDate, 'HH:mm');
              return shiftTime === scheduledInTime;
            });
          }

          // Match all attendance covered by this synced row (combined actuals span multiple punches)
          const coveredAttendance = dayAttendance.filter(attendance =>
            attendanceCoveredBySyncedDay(dayData, attendance)
          );
          coveredAttendance.forEach(attendance => matchedAttendanceIds.add(attendance.id));
          const matchedAttendance =
            coveredAttendance.length > 0
              ? [...coveredAttendance].sort((a, b) =>
                  extractTimezoneNaiveTime(a.clockInTime).localeCompare(
                    extractTimezoneNaiveTime(b.clockInTime)
                  )
                )[0]
              : undefined;

          // When a specific location is selected but there is no matching
          // attendance at that location for this row, hide actual/paid times
          // from the timecard day for display so that Actual Time values
          // reflect only the selected location.
          // Never hide rows that already have synced actuals — the timecard is
          // location-scoped and attendance API timestamps may use UTC "Z" suffix.
          let displayDayData = dayData;
          if (
            debouncedSelectedLocationId !== null &&
            debouncedSelectedLocationId !== 0 &&
            !matchedAttendance &&
            !dayData.actualIn &&
            // For manual timecard days we always want to show Paid values,
            // even if there is no attendance for the selected location.
            dayData.mode !== 'Manual'
          ) {
            displayDayData = {
              ...dayData,
              actualIn: null,
              actualOut: null,
              actualBreakMinutes: null,
              paidIn: null,
              paidOut: null,
              paidBreakMinutes: null,
              // Hide totalHours when paid times are hidden - otherwise we show hours with no paid in/out,
              // which becomes 0 on re-sync (sync is location-scoped) and confuses users.
              totalHours: 0,
            };
          }

          // Determine if unscheduled (no scheduled times but has actual times)
          const isUnscheduled =
            !displayDayData.scheduledIn &&
            !displayDayData.scheduledOut &&
            !!displayDayData.actualIn;

          // Determine if this should be treated as a "manual" row in the UI.
          // We want manual rows (added via "Add Timecard") to:
          // - Start with Shift and Actual sections blank
          // - Have editable Paid In/Out/Break fields even when there are no Actuals
          //
          // Prefer the explicit mode flag when present, but also treat completely
          // blank rows with no shift/attendance/actuals as manual.
          const isManualRow =
            displayDayData.mode === 'Manual' ||
            (!matchedShift &&
              !matchedAttendance &&
              !displayDayData.scheduledIn &&
              !displayDayData.scheduledOut &&
              !displayDayData.actualIn &&
              !displayDayData.actualOut);

          rows.push({
            date,
            dateStr,
            shift: matchedShift,
            attendanceRecord: matchedAttendance,
            dayData: displayDayData,
            shiftIndex: index,
            isUnscheduled,
            // Mark rows that should be handled as manual in the UI
            // so that Shift and Actual sections stay blank and
            // Paid boxes are always editable.
            isManual: isManualRow
          });
        });

        // Attendance on this day not covered by synced timecard rows (e.g. before re-sync)
        const unscheduledFromSynced = dayAttendance.filter(
          attendance => !matchedAttendanceIds.has(attendance.id)
        );
        const otherRowWindows = dayDataRecords
          .map(dayData => getTimecardDayTimeWindow(dayData))
          .filter((window): window is TimeWindow => window != null);

        groupAttendanceBySchedule(unscheduledFromSynced).forEach((group, groupIndex) => {
          const recordsToShow = attendanceRecordsForUnscheduledGroup(group, otherRowWindows);
          recordsToShow.forEach((attendanceRecord, recordIndex) => {
            if (attendanceMatchesSyncedDayTimes(attendanceRecord, dayDataRecords)) {
              return;
            }
            rows.push({
              date,
              dateStr,
              attendanceRecord,
              shiftIndex: dayDataRecords.length + groupIndex + recordIndex,
              isUnscheduled: true,
            });
            const window = getAttendanceTimeWindow(attendanceRecord);
            if (window) otherRowWindows.push(window);
          });
        });
      } else {
        // No timecard day records yet - build from shifts/attendance (for initial display before sync)
        const matchedAttendanceIds = new Set<number>();
        let shiftIndex = 0;

        // Process each shift - find matching attendance records
        dayShifts.forEach(shift => {
          const overlapping = dayAttendance.filter(attendance => {
            if (matchedAttendanceIds.has(attendance.id)) return false;
            return doesShiftOverlapAttendance(shift, attendance);
          });

          if (overlapping.length === 0) {
            rows.push({
              date,
              dateStr,
              shift,
              shiftIndex: shiftIndex++,
            });
            return;
          }

          groupAttendanceBySchedule(overlapping).forEach(group => {
            group.forEach(a => matchedAttendanceIds.add(a.id));
            rows.push({
              date,
              dateStr,
              shift,
              attendanceRecord: group.length === 1 ? group[0] : buildCombinedAttendanceRow(group),
              shiftIndex: shiftIndex++,
            });
          });
        });

        // Group unscheduled attendance records (those without matching shifts) and combine them
        const unscheduledAttendance = dayAttendance.filter(attendance =>
          !matchedAttendanceIds.has(attendance.id)
        );

        const otherRowWindows = rows
          .map(row => getTableRowTimeWindow(row))
          .filter((window): window is TimeWindow => window != null);

        groupAttendanceBySchedule(unscheduledAttendance).forEach((group) => {
          const recordsToShow = attendanceRecordsForUnscheduledGroup(group, otherRowWindows);
          recordsToShow.forEach(attendanceRecord => {
            rows.push({
              date,
              dateStr,
              attendanceRecord,
              shiftIndex: shiftIndex++,
              isUnscheduled: true,
            });
            const window = getAttendanceTimeWindow(attendanceRecord);
            if (window) otherRowWindows.push(window);
          });
        });

        // If no shifts and no attendance, show one row with "Unscheduled"
        if (dayShifts.length === 0 && dayAttendance.length === 0) {
          rows.push({
            date,
            dateStr,
            shiftIndex: 0
          });
        }
      }

      // Add leave rows for this date (from LeaveLedger)
      const dayLeaveEntries = leaveEntries.filter(l => l.date === dateStr);
      if (dayLeaveEntries.length > 0) {
        const totalLeaveHours = dayLeaveEntries.reduce((sum, l) => sum + (l.hours ?? l.days * 8), 0);
        const sourceLabels = dayLeaveEntries.map(l => l.notes || (l.sourceType === 'AnnualLeave' ? 'Annual Leave' : l.sourceType === 'PublicHoliday' ? 'Public Holiday' : l.sourceType === 'UnpaidLeave' ? 'Unpaid Leave' : l.sourceType));
        const uniqueLabels = Array.from(new Set(sourceLabels)).filter(Boolean);
        rows.push({
          date,
          dateStr,
          shiftIndex: rows.filter(r => r.dateStr === dateStr).length,
          isLeave: true,
          leaveHours: totalLeaveHours,
          leaveSourceType: uniqueLabels.join(' / ') || dayLeaveEntries[0].sourceType,
        });
      }
      
      // Add manual timecard rows for this date (if they don't have a dayData record)
      manualTimecardRows.forEach(manualRowKey => {
        const [rowDateStr, rowShiftIndexStr] = manualRowKey.split('-');
        if (rowDateStr === dateStr) {
          const rowShiftIndex = parseInt(rowShiftIndexStr);
          // Only add if this manual row doesn't already exist as a dayData row
          const existingRow = rows.find(r => r.dateStr === dateStr && r.shiftIndex === rowShiftIndex);
          if (!existingRow) {
            rows.push({
              date,
              dateStr,
              dayData: timecard?.days?.find(d => d.date === dateStr),
              shiftIndex: rowShiftIndex,
              isManual: true
            });
          }
        }
      });
    });

    // Helper to get sortable time for a row: Paid In (for manual) > Actual In > empty
    // Manual rows use paidIn for ordering; others use actualIn. Empty sorts last (bottom).
    const getSortTime = (row: TimecardRow): string => {
      const paidIn = row.dayData?.paidIn ? extractTime(row.dayData.paidIn) : null;
      if (paidIn && paidIn !== '--:--') {
        const dateStr = row.dayData?.date ? (row.dayData.date.replace(/T.*/, '') || row.dateStr) : row.dateStr;
        return `${dateStr}T${paidIn}:00`;
      }
      if (row.dayData?.actualIn) {
        const actualInStr = row.dayData.actualIn.trim();
        if (actualInStr.includes(' ') && !actualInStr.includes('T')) {
          return actualInStr.replace(' ', 'T').replace(/\.\d+$/, '');
        }
        return actualInStr.replace(/\.\d+$/, '');
      }
      if (row.attendanceRecord?.clockInTime) {
        return row.attendanceRecord.clockInTime;
      }
      return ''; // No time - sorts last (bottom)
    };
    
    // Helper to get shift In time value for sorting
    const getShiftInTime = (row: TimecardRow): number => {
      if (row.shift) {
        const shiftTime = parseISO(row.shift.startTime);
        return shiftTime.getTime();
      }
      return Infinity; // Unscheduled records
    };

    // Sort rows: by date, then by Paid In / Actual In (rows with no time always last/bottom)
    rows.sort((a, b) => {
      if (a.dateStr !== b.dateStr) {
        return a.dateStr.localeCompare(b.dateStr);
      }
      const aTime = getSortTime(a);
      const bTime = getSortTime(b);
      if (aTime && bTime) return aTime.localeCompare(bTime);
      if (aTime && !bTime) return -1; // a first (has time)
      if (!aTime && bTime) return 1;  // b first (a has no time → a goes last)
      // Both empty: scheduled first (by shift time), manual rows last
      const aShift = getShiftInTime(a);
      const bShift = getShiftInTime(b);
      if (aShift === Infinity && bShift === Infinity) return (a.dayData?.id ?? 0) - (b.dayData?.id ?? 0);
      return aShift - bShift;
    });

    return rows;
  }, [weekDates, shifts, attendanceRecords, timecard, manualTimecardRows, leaveEntries]);

  const refetchTimecardData = async () => {
    if (!selectedEmployeeId) return;
    
    try {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      const weekEndStr = format(weekEnd, 'yyyy-MM-dd');
      
      const shiftsData = await shiftApi.getAll({
        employeeId: selectedEmployeeId,
        startDate: weekStartStr,
        endDate: weekEndStr,
        locationId: selectedLocationId || undefined
      });
      setShifts(shiftsData);
      
      const attendanceData = await attendanceApi.getAll({
        employeeId: selectedEmployeeId,
        startDate: weekStartStr,
        endDate: weekEndStr,
        locationId: selectedLocationId || undefined
      });
      setAttendanceRecords(attendanceData);
      
      const updatedTimecard = await timecardApi.getByEmployeeAndWeek(selectedEmployeeId, weekStartStr, selectedLocationId ?? undefined);
      if (updatedTimecard) {
        setTimecard(updatedTimecard);
      }
      // Refresh sidebar total hours
      if (selectedLocationId && selectedLocationId !== 0) {
        const all = await timecardApi.getAll(undefined, weekStartStr, selectedLocationId);
        setAllTimecardsForWeek(all);
      }
    } catch (error) {
      console.error('Error refetching timecard data:', error);
    }
  };

  // Re-sync: run sync for selected location, then refetch
  const handleResync = async () => {
    if (isSelectedLocationInactive || syncAllInProgress) return;
    if (!selectedEmployeeId || timecard?.isApproved) return;
    
    setLoading(true);
    try {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      await timecardApi.sync(selectedEmployeeId, weekStartStr, selectedLocationId ?? undefined);
      await refetchTimecardData();
    } catch (error) {
      console.error('Error re-syncing timecard:', error);
      alert('Error re-syncing: ' + (error as any)?.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleHardResync = async () => {
    if (isSelectedLocationInactive || syncAllInProgress) return;
    if (!selectedEmployeeId || timecard?.isApproved) return;

    setHardResyncLoading(true);
    setHardResyncError(null);
    try {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      await timecardApi.sync(selectedEmployeeId, weekStartStr, selectedLocationId ?? undefined, true);
      setShowHardResyncConfirm(false);
      await refetchTimecardData();
    } catch (error: any) {
      console.error('Error hard re-syncing timecard:', error);
      setHardResyncError(
        error?.response?.data?.message || error?.message || 'Hard re-sync failed. Please try again.'
      );
    } finally {
      setHardResyncLoading(false);
    }
  };

  const handleSyncAll = async () => {
    if (isSelectedLocationInactive || syncAllInProgress) return;

    const weekStartStr = format(weekStart, 'yyyy-MM-dd');
    const employeesToSync = employeesForBulkSync.filter(employee => {
      const empTimecard = allTimecardsForWeek.find(tc => tc.employeeId === employee.id);
      return !empTimecard?.isApproved;
    });

    if (employeesToSync.length === 0) return;

    setSyncAllInProgress(true);
    setSyncAllProgress(0);
    setSyncAllStatus('Starting sync…');
    isSyncingRef.current = true;

    let completed = 0;
    const total = employeesToSync.length;
    let hadError = false;

    try {
      for (const employee of employeesToSync) {
        setSyncAllStatus(`Syncing ${employee.firstName} ${employee.lastName}…`);
        try {
          await timecardApi.sync(employee.id, weekStartStr, selectedLocationId ?? undefined);
        } catch (error) {
          console.error(`Error syncing employee ${employee.id}:`, error);
          hadError = true;
        }
        completed += 1;
        setSyncAllProgress(Math.round((completed / total) * 100));
      }

      setSyncAllStatus('Refreshing timecards…');
      const all = await timecardApi.getAll(undefined, weekStartStr, selectedLocationId ?? undefined);
      setAllTimecardsForWeek(all);
      await refreshWeekActivityEmployeeIds();
      if (selectedEmployeeId) {
        await refetchTimecardData();
      }
      setSyncAllStatus(hadError ? 'Completed with some errors' : 'Sync complete');
    } catch (error) {
      console.error('Error during sync all:', error);
      setSyncAllStatus('Sync failed');
    } finally {
      isSyncingRef.current = false;
      setTimeout(() => {
        setSyncAllInProgress(false);
        setSyncAllProgress(0);
        setSyncAllStatus('');
      }, hadError ? 1200 : 600);
    }
  };

  // Debug sync: run sync-step1 pipeline and show step-by-step calculation modal
  const handleDebugSync = async () => {
    if (!selectedEmployeeId || !selectedLocationId || selectedLocationId === 0) return;

    setDebugSyncLoading(true);
    try {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      const result = await timecardApi.syncStep1(
        selectedEmployeeId,
        weekStartStr,
        selectedLocationId ?? undefined
      );
      setSyncStep1Data(result.shifts ?? []);
      setSyncStep2Data(result.attendanceRecords ?? []);
      setSyncStep3Data(result.step3Records ?? []);
      setSyncStep4Data(result.step4Records ?? []);
      setSyncStep5Data(result.step5Records ?? []);
      setSyncStep7Data(result.step7Records ?? []);
      setSyncStep8Data(result.step8Records ?? []);
      setSyncStep9Data(result.step9Records ?? []);
      setSyncStep10Data(result.step10Records ?? []);
      setShowSyncStep1Modal(true);
    } catch (error: any) {
      console.error('Error running debug sync:', error);
      alert(
        'Error running debug sync: ' +
          (error?.response?.data?.message || error?.message || 'Unknown error')
      );
    } finally {
      setDebugSyncLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    if (isSelectedLocationInactive) return;
    if (!timecard || timecard.isApproved) return;
    setSavingNotes(true);
    try {
      await timecardApi.updateNotes(timecard.id, notesDraft.trim() || null);
      setTimecard(prev => prev ? { ...prev, notes: notesDraft.trim() || null } : null);
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Error saving notes. Please try again.');
    } finally {
      setSavingNotes(false);
    }
  };

  // Handle timecard approval - once approved, sync should not be run again
  const handleApproveTimecard = async () => {
    if (isSelectedLocationInactive) return;
    if (!timecard || !selectedEmployeeId) return;

    const hasOpenPunch = timecard.days?.some(d => d.mode === 'OpenPunch');
    const hasOverlapError = timecard.days?.some(d => d.syncErrorMessage);
    if (hasOpenPunch || hasOverlapError) {
      alert('Cannot approve: resolve open punch rows and overlap errors first.');
      return;
    }

    setLoading(true);
    try {
      await timecardApi.approve(timecard.id);

      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      const updatedTimecard = await timecardApi.getByEmployeeAndWeek(selectedEmployeeId, weekStartStr, selectedLocationId ?? undefined);
      if (updatedTimecard) {
        setTimecard(updatedTimecard);
      }
      if (selectedLocationId && selectedLocationId !== 0) {
        const all = await timecardApi.getAll(undefined, weekStartStr, selectedLocationId);
        setAllTimecardsForWeek(all);
      }
    } catch (error) {
      console.error('Error approving timecard:', error);
      alert('Error approving timecard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnapproveTimecard = async () => {
    if (isSelectedLocationInactive) return;
    if (!timecard || !selectedEmployeeId) return;

    setLoading(true);
    try {
      setUnapproveError(null);
      await timecardApi.unapprove(timecard.id);

      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      const updatedTimecard = await timecardApi.getByEmployeeAndWeek(selectedEmployeeId, weekStartStr, selectedLocationId ?? undefined);
      if (updatedTimecard) {
        setTimecard(updatedTimecard);
        setShowApprovedBlockMessage(false);
        setShowUnapproveConfirm(false);
      }
    } catch (error) {
      console.error('Error unapproving timecard:', error);
      setUnapproveError('Failed to unapprove timecard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper: parse "HH:mm" to minutes since midnight; return null if invalid
  const timeToMinutes = (timeStr: string | null | undefined): number | null => {
    if (!timeStr || timeStr === '--:--') return null;
    const parts = timeStr.trim().split(':');
    if (parts.length < 2) return null;
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  };

  // Check if two time ranges overlap (handles overnight shifts)
  const timeRangesOverlap = (
    aIn: number | null,
    aOut: number | null,
    bIn: number | null,
    bOut: number | null
  ): boolean => {
    if (aIn == null || aOut == null || bIn == null || bOut == null) return false;
    let aEnd = aOut;
    let bEnd = bOut;
    if (aEnd <= aIn) aEnd += 24 * 60;
    if (bEnd <= bIn) bEnd += 24 * 60;
    return aIn < bEnd && bIn < aEnd;
  };

  // Helper to convert time string (HH:mm) to DateTime string (yyyy-MM-ddTHH:mm:ss) for API
  const timeToDateTime = (timeStr: string | null | undefined, date: Date): string | undefined => {
    if (!timeStr || timeStr === '--:--') return undefined;
    try {
      // If already a DateTime string, return as-is
      if (timeStr.includes('T')) {
        return timeStr;
      }
      // If it's a time string (HH:mm), combine with date
      const [hours, minutes] = timeStr.split(':').map(Number);
      if (!isNaN(hours) && !isNaN(minutes)) {
        const dateTime = new Date(date);
        dateTime.setHours(hours, minutes, 0, 0);
        return format(dateTime, "yyyy-MM-dd'T'HH:mm:ss");
      }
      return undefined;
    } catch {
      return undefined;
    }
  };

  // Helper function to get 2-letter code from name
  // If 2+ words: First letter of first word + First letter of second word (e.g., "Accounting Officer" → "AO")
  // If single word: First 2 letters (e.g., "Manager" → "MA")
  const getTwoLetterCode = (name: string | null | undefined): string => {
    if (!name) return '';
    const trimmed = name.trim();
    if (!trimmed) return '';
    
    // Split by spaces and filter out empty strings
    const words = trimmed.split(/\s+/).filter(w => w.length > 0);
    
    if (words.length >= 2) {
      // Two or more words: First letter of first word + First letter of second word
      return (words[0][0] + words[1][0]).toUpperCase();
    } else if (words.length === 1) {
      // Single word: First 2 letters
      const word = words[0].toUpperCase();
      return word.length >= 2 ? word.substring(0, 2) : word.padEnd(2, 'X');
    }
    
    return '';
  };

  // Get available schedules for the selected employee
  // Include schedules from employee profile AND from shifts (in case schedule was removed from profile but shift still references it)
  const availableSchedules = useMemo(() => {
    if (!selectedEmployeeId) return [];
    const employee = employees.find(emp => emp.id === selectedEmployeeId);
    const scheduleIds = employee?.profile?.scheduleIds || [];
    
    // Get schedule IDs from shifts for the current week
    const shiftScheduleIds = new Set<number>();
    shifts.forEach(shift => {
      if (shift.scheduleId) {
        shiftScheduleIds.add(shift.scheduleId);
      }
    });
    
    // Combine profile schedules and shift schedules
    const allScheduleIds = new Set([...scheduleIds, ...Array.from(shiftScheduleIds)]);
    
    return schedules.filter(s => s.isActive && allScheduleIds.has(s.id));
  }, [selectedEmployeeId, employees, schedules, shifts]);

  // Get available positions for the selected employee
  // Include positions from employee profile AND from shifts (in case position was removed from profile but shift still references it)
  const availablePositions = useMemo(() => {
    if (!selectedEmployeeId) return [];
    const employee = employees.find(emp => emp.id === selectedEmployeeId);
    const mainPositionId = employee?.profile?.mainPositionId;
    const subPositionIds = employee?.profile?.subPositionIds || [];
    const profilePositionIds = [mainPositionId, ...subPositionIds].filter((id): id is number => id !== undefined && id !== null);
    
    // Get position IDs from shifts for the current week
    const shiftPositionIds = new Set<number>();
    shifts.forEach(shift => {
      if (shift.positionId) {
        shiftPositionIds.add(shift.positionId);
      }
    });
    
    // Combine profile positions and shift positions
    const allPositionIds = new Set([...profilePositionIds, ...Array.from(shiftPositionIds)]);
    
    return positions.filter(p => p.isActive && allPositionIds.has(p.id));
  }, [selectedEmployeeId, employees, positions, shifts]);

  const APPROVED_BLOCK_MESSAGE = 'Timecard is already approved. Please unapprove first to make changes.';

  // Handle adding manual timecard row
  const handleAddManualTimecard = async (dateStr: string) => {
    if (isSelectedLocationInactive) return;
    if (!selectedEmployeeId) return;
    if (timecard?.isApproved) {
      setShowApprovedBlockMessage(true);
      return;
    }
    setOpenMenuRow(null);
    
    try {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');

      // Ensure timecard exists
      if (!timecard || !timecard.id || timecard.id === 0) {
        // Create new timecard with a manual day entry
        const newTimecard = await timecardApi.create({
          employeeId: selectedEmployeeId,
          weekStartDate: weekStartStr,
          days: [{
            date: dateStr,
            mode: 'Manual',
            extras: 0,
            totalHours: 0
          }]
        });
        setTimecard(newTimecard);
      } else {
        // Timecard exists - always add a new manual day row for this date
        await timecardApi.addDay(timecard.id, {
          date: dateStr,
          mode: 'Manual',
          extras: 0,
          totalHours: 0
        });

        // Refetch timecard to get the updated data (including the new row)
        await refetchTimecardData();
      }
    } catch (error: any) {
      console.error('Error adding manual timecard:', error);
      if (error.response?.status === 409) {
        alert('A timecard entry for this date already exists.');
      } else {
        alert('Failed to add timecard. Please try again.');
      }
    }
  };

  // Handle deleting manual timecard row
  const handleDeleteManualTimecard = async (dateStr: string, shiftIndex: number, dayDataId?: number) => {
    if (isSelectedLocationInactive) return;
    if (timecard?.isApproved) {
      setShowApprovedBlockMessage(true);
      return;
    }
    setOpenMenuRow(null);
    const manualRowKey = `${dateStr}-${shiftIndex}`;
    setManualTimecardRows(prev => {
      const newSet = new Set(prev);
      newSet.delete(manualRowKey);
      return newSet;
    });
    if (timecard?.id && dayDataId) {
      try {
        await timecardApi.deleteDay(timecard.id, dayDataId);
        await refetchTimecardData();
      } catch (error) {
        console.error('Error deleting timecard day:', error);
        alert('Failed to delete timecard.');
      }
    }
  };

  // Handle day update
  const handleDayUpdate = async (dateStr: string, shiftId: number | undefined, updates: Partial<TimecardDay>, dayDataId?: number) => {
    if (isSelectedLocationInactive) return;
    if (!timecard || !selectedEmployeeId) return;
    if (timecard.isApproved) {
      setShowApprovedBlockMessage(true);
      return;
    }

    // If dayDataId is provided, use it to find the specific dayData record
    // Otherwise, fall back to finding by dateStr (for backward compatibility)
    const dayData = dayDataId 
      ? timecard.days.find(d => d.id === dayDataId)
      : timecard.days.find(d => d.date === dateStr);

    if (!timecard.id || timecard.id === 0) {
      // Create new timecard with updated day data
      try {
        const daysToCreate = timecard.days.map(d => {
          if (d.date === dateStr) {
            return { ...d, ...updates };
          }
          return d;
        });

        const newTimecard = await timecardApi.create({
          employeeId: selectedEmployeeId,
          weekStartDate: format(weekStart, 'yyyy-MM-dd'),
          days: daysToCreate.map(d => ({
            date: d.date,
            mode: d.mode,
            scheduleCode: d.scheduleCode || undefined,
            positionCode: d.positionCode || undefined,
            scheduleId: d.scheduleId ?? undefined,
            positionId: d.positionId ?? undefined,
            extras: d.extras,
            totalHours: d.totalHours,
            paidIn: d.paidIn || undefined,
            paidOut: d.paidOut || undefined,
            paidBreakMinutes: d.paidBreakMinutes || undefined,
            paidBreakPaid: d.paidBreakPaid
          }))
        });
        setTimecard(newTimecard);
      } catch (error) {
        console.error('Error creating timecard:', error);
      }
    } else if (dayData) {
      // Overlap validation: block save if new paid range overlaps with other rows for same date
      if (updates.paidIn !== undefined || updates.paidOut !== undefined) {
        const effectivePaidIn = extractTime(updates.paidIn ?? dayData.paidIn ?? undefined);
        const effectivePaidOut = extractTime(updates.paidOut ?? dayData.paidOut ?? undefined);
        const myIn = timeToMinutes(effectivePaidIn);
        const myOut = timeToMinutes(effectivePaidOut);
        if (myIn != null && myOut != null) {
          const sameDateDays = timecard.days.filter(d => {
            if (!d.date) return false;
            const dDate = /^\d{4}-\d{2}-\d{2}$/.test(d.date) ? d.date : (d.date.replace(/T.*/, '') ?? '');
            if (dDate !== dateStr) return false;
            if (dayDataId && d.id === dayDataId) return false;
            return true;
          });
          for (const other of sameDateDays) {
            const otherIn = timeToMinutes(extractTime(other.paidIn));
            const otherOut = timeToMinutes(extractTime(other.paidOut));
            if (otherIn != null && otherOut != null && timeRangesOverlap(myIn, myOut, otherIn, otherOut)) {
              setOverlapError(`The Paid In/Out range overlaps with another timecard for ${format(parseISO(dateStr), 'EEEE')} ${formatCompanyDate(dateStr)}. Please adjust the times.`);
              return;
            }
          }
        }
      }

      // Update existing day
      setSavingDays(prev => new Set(prev).add(dayData.id));
      try {
        // Convert null values to undefined for API call and only include defined fields
        // Convert time strings (HH:mm) to DateTime strings (yyyy-MM-ddTHH:mm:ss) for API
        const apiUpdates: UpdateTimecardDayRequest = {};
        if (updates.scheduledIn !== undefined) apiUpdates.scheduledIn = timeToDateTime(updates.scheduledIn ?? undefined, dayData.date ? parseISO(dayData.date) : new Date());
        if (updates.scheduledOut !== undefined) apiUpdates.scheduledOut = timeToDateTime(updates.scheduledOut ?? undefined, dayData.date ? parseISO(dayData.date) : new Date());
        if (updates.scheduledBreakMinutes !== undefined) apiUpdates.scheduledBreakMinutes = updates.scheduledBreakMinutes ?? undefined;
        const dayDate = dayData.date ? parseISO(dayData.date) : new Date();
        if (updates.paidIn !== undefined) apiUpdates.paidIn = timeToDateTime(updates.paidIn ?? undefined, dayDate);
        if (updates.paidOut !== undefined) {
          const paidInStr = extractTime(updates.paidIn ?? dayData.paidIn ?? undefined);
          const paidOutStr = extractTime(updates.paidOut ?? undefined);
          const isOvernight = paidInStr && paidOutStr && paidInStr !== '--:--' && paidOutStr !== '--:--' && (() => {
            const [inH, inM] = paidInStr.split(':').map(Number);
            const [outH, outM] = paidOutStr.split(':').map(Number);
            return outH * 60 + outM <= inH * 60 + inM;
          })();
          apiUpdates.paidOut = timeToDateTime(updates.paidOut ?? undefined, isOvernight ? addDays(dayDate, 1) : dayDate);
        }
        if (updates.paidBreakMinutes !== undefined) apiUpdates.paidBreakMinutes = updates.paidBreakMinutes ?? undefined;
        if (updates.unpaidBreakMinutes !== undefined) apiUpdates.unpaidBreakMinutes = updates.unpaidBreakMinutes ?? undefined;
        if (updates.scheduleCode !== undefined) apiUpdates.scheduleCode = updates.scheduleCode ?? undefined;
        if (updates.positionCode !== undefined) apiUpdates.positionCode = updates.positionCode ?? undefined;
        if (typeof updates.scheduleId === 'number') apiUpdates.scheduleId = updates.scheduleId;
        if (typeof updates.positionId === 'number') apiUpdates.positionId = updates.positionId;
        if (typeof updates.isPayExtra === 'boolean') apiUpdates.isPayExtra = updates.isPayExtra;
        if (updates.extras !== undefined) apiUpdates.extras = updates.extras;
        
        if (updates.paidIn !== undefined || updates.paidOut !== undefined || updates.paidBreakMinutes !== undefined || updates.unpaidBreakMinutes !== undefined) {
          const paidInTime = extractTime(updates.paidIn ?? dayData.paidIn ?? undefined);
          const paidOutTime = extractTime(updates.paidOut ?? dayData.paidOut ?? undefined);
          const unpaidBreakMins = updates.unpaidBreakMinutes ?? dayData.unpaidBreakMinutes ?? 0;

          if (paidInTime && paidOutTime && paidInTime !== '--:--' && paidOutTime !== '--:--') {
            const [inHours, inMins] = paidInTime.split(':').map(Number);
            const [outHours, outMins] = paidOutTime.split(':').map(Number);
            const inMinutes = inHours * 60 + inMins;
            let outMinutes = outHours * 60 + outMins;
            if (outMinutes <= inMinutes) {
              outMinutes += 24 * 60;
            }
            const grossMinutes = outMinutes - inMinutes;
            const totalMinutes = Math.max(0, grossMinutes - unpaidBreakMins);
            apiUpdates.totalHours = Math.round((totalMinutes / 60) * 100) / 100;
          }
        }
        
        if (updates.totalHours !== undefined) apiUpdates.totalHours = updates.totalHours;
        
        console.log('[Timecard] handleDayUpdate SENDING:', {
          timecardId: timecard.id,
          dayId: dayData.id,
          dateStr,
          locationId: selectedLocationId,
          apiUpdates: JSON.stringify(apiUpdates)
        });
        await timecardApi.updateDay(timecard.id, dayData.id, apiUpdates);
        // Refetch timecard to ensure we have the latest data for color matching
        const weekStartStr = format(weekStart, 'yyyy-MM-dd');
        const updatedTimecard = await timecardApi.getByEmployeeAndWeek(selectedEmployeeId, weekStartStr, selectedLocationId ?? undefined);
        const updatedDay = updatedTimecard?.days.find(d => d.id === dayData.id);
        console.log('[Timecard] handleDayUpdate AFTER REFETCH:', {
          dateStr,
          locationId: selectedLocationId,
          updatedDay: updatedDay ? {
            id: updatedDay.id,
            date: updatedDay.date,
            mode: updatedDay.mode,
            paidIn: updatedDay.paidIn,
            paidOut: updatedDay.paidOut,
            paidBreakMinutes: updatedDay.paidBreakMinutes,
            totalHours: updatedDay.totalHours
          } : 'NOT FOUND'
        });
        if (updatedTimecard) {
          setTimecard(updatedTimecard);
        } else {
          // Fallback: Update local state if refetch fails
        setTimecard(prev => {
          if (!prev) return null;
          return {
            ...prev,
            days: prev.days.map(d =>
              d.date === dateStr ? { ...d, ...updates } : d
            )
          };
        });
        }
      } catch (error) {
        console.error('Error updating timecard day:', error);
      } finally {
        setSavingDays(prev => {
          const next = new Set(prev);
          if (dayData) next.delete(dayData.id);
          return next;
        });
      }
    }
  };

  // Format time for display
  const formatTime = (timeStr: string | null | undefined): string => {
    if (!timeStr) return '--:--';
    return timeStr;
  };

  // Total hours for selected location (timecard only)
  const selectedLocationTotalHours = useMemo(() => {
    return sumHoursInCurrentWeek(timecard?.days);
  }, [timecard, sumHoursInCurrentWeek]);

  // Calculate leave hours for the week
  const leaveTotalHours = useMemo(() => {
    return leaveEntries.reduce((sum, l) => sum + (l.hours ?? l.days * 8), 0);
  }, [leaveEntries]);

  // Grand total: sum of all location totals + leave (used when multiple locations or leave)
  const grandTotalHours = useMemo(() => {
    const locationsSum = otherLocationTotals.reduce((sum, t) => sum + t.totalHours, 0);
    return locationsSum + leaveTotalHours;
  }, [otherLocationTotals, leaveTotalHours]);

  // Display total for selected location (includes leave when viewing single location)
  const weeklyTotalHours = selectedLocationTotalHours + leaveTotalHours;

  const extrasTotalForWeek = useMemo(() => {
    return timecard?.days?.reduce((sum, d) => sum + (d.extras ?? 0), 0) ?? 0;
  }, [timecard]);

  const compareEmployeeByName = (a: Employee, b: Employee): number => {
    const firstNameCompare = (a.firstName || '')
      .trim()
      .toLowerCase()
      .localeCompare((b.firstName || '').trim().toLowerCase());
    if (firstNameCompare !== 0) {
      return firstNameCompare;
    }
    return (a.lastName || '')
      .trim()
      .toLowerCase()
      .localeCompare((b.lastName || '').trim().toLowerCase());
  };

  const getEmployeeSidebarHours = (employeeId: number): number => {
    const isSelected = employeeId === selectedEmployeeId;
    const cachedTimecard = allTimecardsForWeek.find(tc => tc.employeeId === employeeId);

    // Only use live timecard when it belongs to this employee — avoids flashing the
    // previous employee's hours when clicking a new row in the sidebar.
    let workHrs = sumHoursInCurrentWeek(cachedTimecard?.days);
    if (isSelected && timecard?.employeeId === employeeId) {
      workHrs = sumHoursInCurrentWeek(timecard?.days) || workHrs;
    }

    const leaveHrs =
      isSelected && timecard?.employeeId === employeeId ? leaveTotalHours : 0;
    return workHrs + leaveHrs;
  };

  const empCounts = useMemo(() => {
    let base = [...employees];
    if (debouncedSelectedLocationId !== null && debouncedSelectedLocationId !== 0) {
      base = base.filter(employee => employeeMatchesSelectedLocation(employee));
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      base = base.filter(
        emp =>
          `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(query) ||
          (emp.email || '').toLowerCase().includes(query),
      );
    }
    const approvedCount = base.filter(
      e => allTimecardsForWeek.find(t => t.employeeId === e.id)?.isApproved ?? false,
    ).length;
    return { all: base.length, approved: approvedCount, unapproved: base.length - approvedCount };
  }, [employees, debouncedSelectedLocationId, searchQuery, allTimecardsForWeek, employeeMatchesSelectedLocation]);

  // Filter and sort employees by location, then hours (>0 first), then name
  const filteredEmployees = useMemo(() => {
    if (!employees || employees.length === 0) {
      return [];
    }
    
    let filtered = [...employees];
    
    // Apply location filter from top banner (if selected)
    if (debouncedSelectedLocationId !== null && debouncedSelectedLocationId !== 0) {
      filtered = filtered.filter((employee) => employeeMatchesSelectedLocation(employee));
    }
    
    // Apply search filter if query exists
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(emp =>
        `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(query) ||
        (emp.email || '').toLowerCase().includes(query)
      );
    }

    if (empFilter !== 'all') {
      filtered = filtered.filter(emp => {
        const tc = allTimecardsForWeek.find(t => t.employeeId === emp.id);
        const approved = tc?.isApproved ?? false;
        return empFilter === 'approved' ? approved : !approved;
      });
    }
    
    // Employees with hours first (sorted by name), then zero-hour employees (sorted by name)
    const withHours = filtered
      .filter(emp => getEmployeeSidebarHours(emp.id) > 0)
      .sort(compareEmployeeByName);
    const zeroHours = filtered
      .filter(emp => getEmployeeSidebarHours(emp.id) <= 0)
      .sort(compareEmployeeByName);

    return [...withHours, ...zeroHours];
  }, [employees, searchQuery, empFilter, debouncedSelectedLocationId, allTimecardsForWeek, selectedEmployeeId, timecard, leaveTotalHours, employeeMatchesSelectedLocation]);

  const employeesForBulkSync = useMemo(() => {
    if (!employees || employees.length === 0) {
      return [];
    }

    if (debouncedSelectedLocationId !== null && debouncedSelectedLocationId !== 0) {
      return employees.filter((employee) => employeeMatchesSelectedLocation(employee));
    }

    return employees;
  }, [employees, debouncedSelectedLocationId, employeeMatchesSelectedLocation]);

  const syncAllEmployeeCount = useMemo(
    () =>
      employeesForBulkSync.filter(employee => {
        const empTimecard = allTimecardsForWeek.find(tc => tc.employeeId === employee.id);
        return !empTimecard?.isApproved;
      }).length,
    [employeesForBulkSync, allTimecardsForWeek]
  );

  // Reset selection and data when no specific location is selected
  useEffect(() => {
    if (!debouncedSelectedLocationId || debouncedSelectedLocationId === 0) {
      if (selectedEmployeeId !== null) {
        setSelectedEmployeeId(null);
      }
      setTimecard(null);
      setShifts([]);
      setAttendanceRecords([]);
    }
  }, [debouncedSelectedLocationId, selectedEmployeeId]);

  // If location changes and current employee is no longer visible, clear the selection
  useEffect(() => {
    if (
      debouncedSelectedLocationId !== null &&
      debouncedSelectedLocationId !== 0 &&
      selectedEmployeeId &&
      !filteredEmployees.some(e => e.id === selectedEmployeeId)
    ) {
      setSelectedEmployeeId(null);
    }
  }, [debouncedSelectedLocationId, filteredEmployees, selectedEmployeeId]);

  // Scroll to selected employee only on initial load (when employee is remembered from localStorage)
  useEffect(() => {
    // Only scroll on initial load, not when user manually selects an employee
    if (!hasScrolledToInitialEmployee.current && selectedEmployeeId && selectedEmployeeRef.current && employeeListRef.current && filteredEmployees.length > 0) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const container = employeeListRef.current;
        const selectedElement = selectedEmployeeRef.current;
        if (container && selectedElement) {
          const containerRect = container.getBoundingClientRect();
          const elementRect = selectedElement.getBoundingClientRect();
          const scrollTop = container.scrollTop;
          const elementTop = elementRect.top - containerRect.top + scrollTop;
          const elementHeight = elementRect.height;
          
          // Calculate position to show selected employee at 3rd row (2 rows above it)
          // Assuming average row height, we want to show 2 rows above the selected employee
          // Use the actual element height to calculate spacing
          const rowsAbove = 2; // Show 2 rows above (making selected employee the 3rd row)
          const offsetFromTop = rowsAbove * elementHeight;
          
          // Scroll so that the selected employee appears at the 3rd row position
          const targetScroll = elementTop - offsetFromTop;
          container.scrollTo({
            top: Math.max(0, targetScroll),
            behavior: 'smooth'
          });
          
          // Mark that we've done the initial scroll
          hasScrolledToInitialEmployee.current = true;
        }
      }, 100);
    }
  }, [selectedEmployeeId, filteredEmployees]);

  // If no specific location is selected, show info message instead of timecards UI
  if (!debouncedSelectedLocationId || debouncedSelectedLocationId === 0) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center text-gray-500 max-w-md text-center px-4">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
            <MapPin className="w-8 h-8 text-blue-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Select a Location to View Timecards
          </h2>
          <p className="text-sm">
            Timecards are scoped by location. Please choose a location from the top location dropdown to see the employee list and their timecards.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-gray-50 overflow-hidden relative">
      {syncAllInProgress && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="sync-all-title"
          aria-describedby="sync-all-description"
        >
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-600 mb-4" aria-hidden />
            <h3 id="sync-all-title" className="text-base font-semibold text-gray-900">
              Syncing all employees
            </h3>
            <p id="sync-all-description" className="text-sm text-gray-600 mt-2 min-h-[1.25rem]">
              {syncAllStatus}
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-4 overflow-hidden">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${syncAllProgress}%` }}
              />
            </div>
            <p className="text-2xl font-semibold text-gray-900 mt-3">{syncAllProgress}%</p>
            <p className="text-xs text-gray-500 mt-2">Please wait — page actions are temporarily disabled.</p>
          </div>
        </div>
      )}

      {/* Employee List Sidebar */}
      <div data-timecards-sidebar className="w-64 bg-white border-r border-gray-200 flex flex-col h-full">
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">
            {formatCompanyDateRange(weekStart, weekEnd)}
          </h2>
          <button
            type="button"
            onClick={handleSyncAll}
            disabled={
              syncAllInProgress ||
              isSelectedLocationInactive ||
              syncAllEmployeeCount === 0
            }
            className="flex items-center space-x-2 px-4 py-2 mb-3 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              isSelectedLocationInactive
                ? 'Inactive location — view only.'
                : syncAllEmployeeCount === 0
                  ? 'No employees available to sync (all approved or none assigned).'
                  : `Sync timecards for ${syncAllEmployeeCount} employee(s) for this week`
            }
          >
            <RefreshCw className={`w-4 h-4 ${syncAllInProgress ? 'animate-spin' : ''}`} />
            <span>Sync All</span>
          </button>
          {/* Search Box */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search employees..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={syncAllInProgress}
              className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {(['all', 'approved', 'unapproved'] as const).map(filter => (
              <button
                key={filter}
                type="button"
                onClick={() => setEmpFilter(filter)}
                disabled={syncAllInProgress}
                className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors disabled:opacity-50 ${
                  empFilter === filter
                    ? 'bg-blue-100 text-blue-800 border-blue-300'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {filter === 'all' ? 'All' : filter === 'approved' ? 'Approved' : 'Unapproved'}
                {' '}({empCounts[filter]})
              </button>
            ))}
          </div>
        </div>
        <div 
          ref={employeeListRef}
          className="flex-1 overflow-y-auto min-h-0"
        >
          {referenceDataLoading && filteredEmployees.length === 0 ? (
            <div className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-gray-500">
              <RefreshCw className="w-4 h-4 animate-spin text-gray-400" aria-hidden />
              <span>Loading employees…</span>
            </div>
          ) : null}
          {filteredEmployees.map(employee => {
            const empTimecard = allTimecardsForWeek.find(tc => tc.employeeId === employee.id);
            const isSelected = employee.id === selectedEmployeeId;
            const totalHrs = getEmployeeSidebarHours(employee.id);
            const isApproved = isSelected ? (timecard?.isApproved ?? empTimecard?.isApproved) : empTimecard?.isApproved;
            return (
            <button
              key={employee.id}
              ref={isSelected ? selectedEmployeeRef : null}
              onClick={() => {
                if (syncAllInProgress) return;
                setSelectedEmployeeId(employee.id);
              }}
              disabled={syncAllInProgress}
              className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isSelected ? 'bg-blue-50 border-blue-200' : ''
              }`}
            >
              <div className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                {employee.firstName} {employee.lastName}
                {!employee.isActive && (
                  <span className="text-[10px] uppercase tracking-wide font-semibold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">
                    Inactive
                  </span>
                )}
                {isApproved && (
                  <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" title="Timecard approved" />
                )}
              </div>
              <div className="text-sm text-gray-500 mt-0.5">
                {weekTimecardsLoading && !empTimecard
                  ? '—'
                  : `${totalHrs.toFixed(2)} hrs`}
              </div>
            </button>
          );})}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden h-full">
        {/* Week Navigator */}
        <div className="bg-gray-50 border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Selected Employee Name */}
              {selectedEmployeeId && (() => {
                const selectedEmployee = employees.find(e => e.id === selectedEmployeeId);
                return selectedEmployee ? (
                  <div className="flex items-center space-x-2 px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
                    <span className="text-sm font-medium text-blue-900">
                      {selectedEmployee.firstName} {selectedEmployee.lastName}
                    </span>
                    {(weeklyTotalHours > 0) && (
                      <span
                        className="text-sm font-semibold text-blue-700 border-l border-blue-300 pl-2 ml-1"
                        title={leaveTotalHours > 0 ? `${selectedLocationTotalHours.toFixed(2)} hrs worked + ${leaveTotalHours.toFixed(2)} hrs leave` : undefined}
                      >
                        {weeklyTotalHours.toFixed(2)} hrs
                        {leaveTotalHours > 0 && (
                          <span className="font-normal text-blue-600/90"> (incl. leave)</span>
                        )}
                      </span>
                    )}
                  </div>
                ) : null;
              })()}
              <WeekNavigator
                variant="subtle"
                weekStart={weekStart}
                weekEnd={weekEnd}
                weekStartsOn={weekStartsOn}
                currentWeek={currentWeek}
                onPreviousWeek={() => {
                  if (!syncAllInProgress) setCurrentWeek(subWeeks(currentWeek, 1));
                }}
                onNextWeek={() => {
                  if (!syncAllInProgress) setCurrentWeek(addWeeks(currentWeek, 1));
                }}
                onCurrentWeek={() => {
                  if (!syncAllInProgress) setCurrentWeek(new Date());
                }}
                onWeekSelect={(ymd) => {
                  if (!syncAllInProgress) setCurrentWeek(parseLocalDate(ymd));
                }}
                disabled={syncAllInProgress}
                currentWeekLabel="Today"
                dateFormat="long"
              />
            </div>
            {selectedEmployeeId && timecard && (
              <div className="flex flex-wrap items-center gap-3">
                {/* Notes — editable when not approved, read-only when approved */}
                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => setNotesExpanded(!notesExpanded)}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <FileText className="w-4 h-4" />
                    <span>{timecard.notes ? 'View / Edit Notes' : 'Add Notes'}</span>
                    {notesExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {notesExpanded && (
                    <div className="mt-2 p-3 bg-white border border-gray-200 rounded-lg shadow-sm min-w-[280px]">
                      <textarea
                        value={notesDraft}
                        onChange={(e) => setNotesDraft(e.target.value)}
                        placeholder={timecard.isApproved ? 'Notes (read only)' : 'Add notes before approval...'}
                        rows={3}
                        readOnly={!!timecard.isApproved || isSelectedLocationInactive}
                        className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                          timecard.isApproved || isSelectedLocationInactive ? 'bg-gray-100 text-gray-600' : ''
                        }`}
                        maxLength={2000}
                      />
                      {timecard.isApproved ? (
                        <p className="mt-2 text-xs text-amber-800">Timecard is approved — notes are read only.</p>
                      ) : !isSelectedLocationInactive ? (
                        <div className="mt-2 flex justify-end">
                          <button
                            type="button"
                            onClick={handleSaveNotes}
                            disabled={savingNotes}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                          >
                            {savingNotes ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
                {timecard.isApproved ? (
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full bg-green-50 text-green-700 border border-green-200"
                      title="Further changes in attendance or shifts will not sync into this timecard."
                    >
                      Approved
                      <Info className="w-3.5 h-3.5 text-green-600 flex-shrink-0" aria-hidden />
                    </span>
                    <button
                      onClick={() => setShowUnapproveConfirm(true)}
                      disabled={loading || syncAllInProgress || isSelectedLocationInactive}
                      className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Unapprove to allow edits and sync again"
                    >
                      <span>UnApprove</span>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleApproveTimecard}
                    disabled={loading || syncAllInProgress || isSelectedLocationInactive}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>Approve Timecard</span>
                  </button>
                )}
                <button
                  onClick={handleResync}
                  disabled={loading || hardResyncLoading || syncAllInProgress || !!timecard.isApproved || isSelectedLocationInactive}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={isSelectedLocationInactive ? 'Inactive location — view only.' : timecard.isApproved ? 'Re-sync is disabled for approved timecards.' : 'Re-sync from Shifts and Attendance'}
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  <span>Re-sync</span>
                </button>
                <button
                  onClick={() => {
                    setHardResyncError(null);
                    setShowHardResyncConfirm(true);
                  }}
                  disabled={loading || hardResyncLoading || syncAllInProgress || !!timecard.isApproved || isSelectedLocationInactive}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-orange-600 hover:bg-orange-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={isSelectedLocationInactive ? 'Inactive location — view only.' : timecard.isApproved ? 'Hard re-sync is disabled for approved timecards.' : 'Delete all timecard rows and rebuild from Shifts and Attendance'}
                >
                  <RefreshCw className={`w-4 h-4 ${hardResyncLoading ? 'animate-spin' : ''}`} />
                  <span>{hardResyncLoading ? 'Running…' : 'Hard Re-Sync'}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {isSelectedLocationInactive && selectedLocation && (
          <div className="px-4 pt-2">
            <InactiveLocationBanner locationName={selectedLocation.name} className="mb-0" />
          </div>
        )}

        {timecard?.isApproved && showApprovedBlockMessage && (
          <div className="px-4 pt-2">
            <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" aria-hidden />
              <div className="flex-1">
                <div className="font-semibold">Timecard is approved</div>
                <div className="text-xs sm:text-sm">{APPROVED_BLOCK_MESSAGE}</div>
              </div>
              <button
                type="button"
                onClick={() => setShowApprovedBlockMessage(false)}
                className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-full text-amber-500 hover:bg-amber-100 hover:text-amber-700 focus:outline-none"
                aria-label="Dismiss"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          </div>
        )}

        {overlapError && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">Time overlap</h3>
                <button
                  type="button"
                  onClick={() => setOverlapError(null)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <div className="px-4 py-4 text-sm text-gray-700">
                <p>{overlapError}</p>
              </div>
              <div className="flex items-center justify-end border-t px-4 py-3">
                <button
                  type="button"
                  onClick={() => setOverlapError(null)}
                  className="px-3 py-1.5 text-xs sm:text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {deleteConfirm && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">Delete timecard?</h3>
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(null)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <div className="px-4 py-4 space-y-3 text-sm text-gray-700">
                <p>
                  Are you sure you want to delete the timecard for <span className="font-semibold">{deleteConfirm.dateLabel}</span>?
                </p>
                <p>This action cannot be undone.</p>
              </div>
              <div className="flex items-center justify-end gap-3 border-t px-4 py-3">
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(null)}
                  className="px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (deleteConfirm) {
                      await handleDeleteManualTimecard(deleteConfirm.dateStr, deleteConfirm.shiftIndex, deleteConfirm.dayDataId);
                      setDeleteConfirm(null);
                    }
                  }}
                  className="px-3 py-1.5 text-xs sm:text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {showHardResyncConfirm && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">Hard re-sync this timecard?</h3>
                <button
                  type="button"
                  onClick={() => {
                    if (hardResyncLoading) return;
                    setShowHardResyncConfirm(false);
                    setHardResyncError(null);
                  }}
                  disabled={hardResyncLoading}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none disabled:opacity-50"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <div className="px-4 py-4 space-y-3 text-sm text-gray-700">
                <p>
                  This will remove all existing timecard rows for this week and rebuild them from shifts and attendance.
                </p>
                <p className="text-orange-700 bg-orange-50 border border-orange-100 rounded-md px-3 py-2">
                  Any manual edits — including paid time changes and manually added rows — will be permanently lost.
                </p>
                {hardResyncError && (
                  <p className="text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                    {hardResyncError}
                  </p>
                )}
              </div>
              <div className="flex items-center justify-end gap-3 border-t px-4 py-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowHardResyncConfirm(false);
                    setHardResyncError(null);
                  }}
                  disabled={hardResyncLoading}
                  className="px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleHardResync}
                  disabled={hardResyncLoading}
                  className="px-3 py-1.5 text-xs sm:text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md disabled:opacity-50"
                >
                  {hardResyncLoading ? 'Rebuilding…' : 'Yes, hard re-sync'}
                </button>
              </div>
            </div>
          </div>
        )}

        {timecard?.isApproved && showUnapproveConfirm && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">Unapprove timecard?</h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowUnapproveConfirm(false);
                    setUnapproveError(null);
                  }}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <div className="px-4 py-4 space-y-3 text-sm text-gray-700">
                <p>
                  This timecard is currently <span className="font-semibold">approved</span>. Unapproving it will
                  allow further edits and syncing from shifts and attendance.
                </p>
                <p>Are you sure you want to unapprove this timecard?</p>
                {unapproveError && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                    {unapproveError}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-3 border-t px-4 py-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowUnapproveConfirm(false);
                    setUnapproveError(null);
                  }}
                  className="px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUnapproveTimecard}
                  disabled={loading}
                  className="px-3 py-1.5 text-xs sm:text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Unapprove timecard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Timecard Grid */}
        <div className="flex-1 overflow-auto p-6">
          {!selectedEmployeeId ? (
            <div className="flex items-center justify-center h-full text-gray-500">
              Select an employee to view timecard
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : timecard ? (
            <div className="bg-gray-50 rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day</th>
                      <th colSpan={3} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Scheduled</th>
                      <th colSpan={3} className="px-4 py-3 pl-5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actual</th>
                      <th colSpan={4} className="px-4 py-3 pl-5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Paid</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                      {employees.find((e) => e.id === selectedEmployeeId)?.payroll?.allowAdditiveHourlyPay === true && (
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" title="Additive matrix pay on top of salary">
                          Pay Extra
                        </th>
                      )}
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Extras ({companyCurrency})
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Hours</th>
                    </tr>
                    <tr>
                      <th></th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500">In</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500">Out</th>
                      <th className="px-2 py-2 pr-5 text-xs font-medium text-gray-500">Break</th>
                      <th className="px-2 py-2 pl-5 text-xs font-medium text-gray-500">In</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500">Out</th>
                      <th className="px-2 py-2 pr-5 text-xs font-medium text-gray-500">Break</th>
                      <th className="px-2 py-2 pl-5 text-xs font-medium text-gray-500">In</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500">Out</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500" title="Paid break (mins)">Paid br</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500" title="Unpaid break (mins)">Unpd br</th>
                      <th></th>
                      <th></th>
                      {employees.find((e) => e.id === selectedEmployeeId)?.payroll?.allowAdditiveHourlyPay === true && (
                        <th></th>
                      )}
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-50">
                    {tableRows.map((row, index) => {
                      // isUnscheduled is true if attendance exists without matching shift
                      // Also true if no shifts and no attendance for the day
                      const isUnscheduled = row.isUnscheduled || (!row.shift && !row.attendanceRecord);
                      const scheduledIn = row.shift ? extractTime(row.shift.startTime) : null;
                      const scheduledOut = row.shift ? extractTime(row.shift.endTime) : null;
                      const scheduledBreak = row.shift ? resolveShiftBreakMinutes(row.shift, breakTimeSettings) : null;
                      const scheduledBreakIsPaid = row.shift ? resolveShiftBreakIsPaid(row.shift, breakTimeSettings) : false;
                      const scheduledBreakIsGlobal = row.shift ? shiftUsesGlobalBreakSettings(row.shift) : false;
                      
                      // Check if this is a new day (different from previous row)
                      const previousRow = index > 0 ? tableRows[index - 1] : null;
                      const isNewDay = !previousRow || previousRow.dateStr !== row.dateStr;
                      
                      // Show date label on the first row for this date (after sorting), not based on shiftIndex
                      const showDateLabel = isNewDay;
                      // Apply prominent border only between days, not between shifts on same day
                      const borderClass = isNewDay ? 'border-t-2 border-gray-400' : '';

                      // Get actual times from TimecardDay (synced data) if available, otherwise fall back to attendance record
                      // Extract time portion from DateTime strings (API now returns DateTime, but UI displays time only)
                      const actualIn = row.dayData?.actualIn 
                        ? extractTime(row.dayData.actualIn)
                        : (row.attendanceRecord ? extractTime(row.attendanceRecord.clockInTime) : null);
                      const isOpenPunchRow = row.dayData?.mode === 'OpenPunch';
                      const actualOut = row.dayData?.actualOut 
                        ? extractTime(row.dayData.actualOut)
                        : (row.attendanceRecord ? extractTime(row.attendanceRecord.clockOutTime) : null);
                      const actualOutDisplay = isOpenPunchRow
                        ? getOpenPunchOutLabel({
                            shiftEndTime: row.shift?.endTime,
                            actualIn: row.dayData?.actualIn ?? null,
                          })
                        : formatTime(actualOut);
                      const actualBreak = row.dayData?.actualBreakMinutes ?? row.attendanceRecord?.breakMinutes ?? null;

                      // Get paid values for comparison (extract time from DateTime strings)
                      const paidIn = row.dayData?.paidIn ? extractTime(row.dayData.paidIn) : null;
                      const paidOut = row.dayData?.paidOut ? extractTime(row.dayData.paidOut) : null;
                      const paidBreak = row.dayData?.paidBreakMinutes ?? null;
                      const unpaidBreak = row.dayData?.unpaidBreakMinutes ?? null;
                      // Check if there are no Actuals. Normally we disable Paid fields when
                      // there are no Actuals, but for manual rows we WANT Paid to be editable
                      // even when there are no Actuals.
                      const hasNoActuals = !actualIn && !actualOut && !isOpenPunchRow;
                      const isApprovedTimecard = !!timecard?.isApproved;
                      const disablePaidFields =
                        isApprovedTimecard ||
                        isOpenPunchRow ||
                        !!row.dayData?.syncErrorMessage ||
                        (!row.isManual && hasNoActuals);
                      // Helper function to normalize time strings for comparison (handles "HH:mm" format)
                      const normalizeTime = (time: string | null | undefined): string | null => {
                        if (!time) return null;
                        // Remove any whitespace and ensure format is consistent
                        return time.trim().replace(/\s+/g, '');
                      };

                      // Compare values to determine if they match paid values
                      const scheduledInMatchesPaid = scheduledIn && paidIn && normalizeTime(scheduledIn) === normalizeTime(paidIn);
                      const actualInMatchesPaid = actualIn && paidIn && normalizeTime(actualIn) === normalizeTime(paidIn);
                      const bothInMatchPaid = scheduledInMatchesPaid && actualInMatchesPaid;
                      // Check which was last clicked for this day
                      const lastClickedIn = lastClickedField.get(row.dateStr);
                      // If both match Paid, prioritize Actual unless Scheduled was clicked
                      const showScheduledInColor = scheduledInMatchesPaid && (!actualInMatchesPaid || (bothInMatchPaid && lastClickedIn === 'scheduledIn'));
                      
                      const scheduledOutMatchesPaid = scheduledOut && paidOut && normalizeTime(scheduledOut) === normalizeTime(paidOut);
                      const actualOutMatchesPaid = actualOut && paidOut && normalizeTime(actualOut) === normalizeTime(paidOut);
                      const bothOutMatchPaid = scheduledOutMatchesPaid && actualOutMatchesPaid;
                      const lastClickedOut = lastClickedField.get(row.dateStr);
                      // If both match Paid, prioritize Actual unless Scheduled was clicked
                      const showScheduledOutColor = scheduledOutMatchesPaid && (!actualOutMatchesPaid || (bothOutMatchPaid && lastClickedOut === 'scheduledOut'));
                      
                      const scheduledBreakMatchesPaid = scheduledBreak !== null && (
                        scheduledBreakIsPaid
                          ? paidBreak !== null && scheduledBreak === paidBreak
                          : unpaidBreak !== null && scheduledBreak === unpaidBreak
                      );
                      const actualBreakMatchesPaid = actualBreak !== null && paidBreak !== null && actualBreak === paidBreak;
                      const bothBreakMatchPaid = scheduledBreakMatchesPaid && actualBreakMatchesPaid;
                      const lastClickedBreak = lastClickedField.get(row.dateStr);
                      // If both match Paid, prioritize Actual unless Scheduled was clicked
                      const showScheduledBreakColor = scheduledBreakMatchesPaid && (!actualBreakMatchesPaid || (bothBreakMatchPaid && lastClickedBreak === 'scheduledBreak'));

                      // Check if both Actual IN and Actual Out have values (required for clickability)
                      const canClick = !isApprovedTimecard && actualIn && actualOut;

                      // Handler to copy value to Paid box
                      const handleCopyToPaid = (value: string | null | number, field: 'paidIn' | 'paidOut' | 'paidBreakMinutes' | 'unpaidBreakMinutes', source: 'scheduledIn' | 'actualIn' | 'scheduledOut' | 'actualOut' | 'scheduledBreak' | 'actualBreak') => {
                        if (!canClick || value === null || value === undefined) return;
                        // Track which field was clicked
                        setLastClickedField(prev => {
                          const newMap = new Map(prev);
                          newMap.set(row.dateStr, source);
                          return newMap;
                        });
                        // Use row.dayData?.id to ensure we update the correct dayData record
                        const dayDataId = row.dayData?.id;
                        if (field === 'paidBreakMinutes' || field === 'unpaidBreakMinutes') {
                          handleDayUpdate(row.dateStr, row.shift?.id, { [field]: typeof value === 'number' ? value : parseInt(String(value)) || 0 }, dayDataId);
                        } else {
                          handleDayUpdate(row.dateStr, row.shift?.id, { [field]: value }, dayDataId);
                        }
                      };

                      // Leave row - simplified display
                      if (row.isLeave) {
                        return (
                          <tr key={`leave-${row.dateStr}-${row.shiftIndex}`} className={`hover:bg-gray-50 ${borderClass} bg-emerald-50/50`}>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {renderDayColumnLabel(row, showDateLabel)}
                            </td>
                            <td colSpan={9} className="px-4 py-2 text-sm text-emerald-700 font-medium">
                              Leave ({row.leaveSourceType})
                            </td>
                            <td className="px-2 py-3"></td>
                            <td className="px-2 py-3"></td>
                            <td className="px-2 py-3"></td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-emerald-700">
                              {(row.leaveHours ?? 0).toFixed(2)}
                            </td>
                          </tr>
                        );
                      }

                      return (
                        <tr key={`${row.dateStr}-${row.shiftIndex}`} className={`hover:bg-gray-50 ${borderClass}`}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {renderDayColumnLabel(row, showDateLabel)}
                          </td>
                          {/* Scheduled - keep completely blank for manual rows */}
                          {row.isManual ? (
                            <>
                              <td className="px-2 py-3 whitespace-nowrap" colSpan={3}></td>
                            </>
                          ) : (
                            <>
                              <td className={`px-2 py-3 whitespace-nowrap ${isUnscheduled ? 'text-center pr-5' : ''} ${!isUnscheduled ? 'relative' : ''}`} colSpan={isUnscheduled ? 3 : 1}>
                            {isUnscheduled ? (
                                  <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium inline-block">
                                Unscheduled
                              </div>
                            ) : (
                                  <div 
                                    onClick={() => canClick && scheduledIn && handleCopyToPaid(scheduledIn, 'paidIn', 'scheduledIn')}
                                    className={`px-2 py-1 text-sm border rounded text-center ${
                                      showScheduledInColor 
                                        ? 'border-gray-300 bg-green-100 text-green-900'
                                        : 'border-gray-300 bg-gray-50 text-gray-900'
                                    } ${canClick && scheduledIn ? 'cursor-pointer' : ''}`}
                                    title={canClick && scheduledIn ? 'Click to copy to Paid In' : ''}
                                  >
                                    {scheduledIn || '--:--'}
                                  </div>
                            )}
                            {!isUnscheduled && (
                              <div className="absolute top-1/2 left-[calc(100%-0.25rem)] w-3 h-0.5 border-t-2 border-dotted border-gray-400 transform -translate-y-1/2 z-10"></div>
                            )}
                          </td>
                          {!isUnscheduled && (
                            <>
                              <td className="px-2 py-3 whitespace-nowrap">
                                    <div 
                                      onClick={() => canClick && scheduledOut && handleCopyToPaid(scheduledOut, 'paidOut', 'scheduledOut')}
                                      className={`px-2 py-1 text-sm border rounded text-center ${
                                        showScheduledOutColor 
                                          ? 'border-gray-300 bg-red-100 text-red-900'
                                          : 'border-gray-300 bg-gray-50 text-gray-900'
                                      } ${canClick && scheduledOut ? 'cursor-pointer' : ''}`}
                                      title={canClick && scheduledOut ? 'Click to copy to Paid Out' : ''}
                                    >
                                      {scheduledOut || '--:--'}
                                    </div>
                              </td>
                                  <td className="px-2 py-3 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <div 
                                        onClick={() => canClick && scheduledBreak !== null && handleCopyToPaid(
                                          scheduledBreak,
                                          scheduledBreakIsPaid ? 'paidBreakMinutes' : 'unpaidBreakMinutes',
                                          'scheduledBreak'
                                        )}
                                        className={`px-2 py-1 text-sm border rounded text-center ${
                                          showScheduledBreakColor 
                                            ? 'border-gray-300 bg-yellow-100 text-yellow-900'
                                            : 'border-gray-300 bg-gray-50 text-gray-900'
                                        } ${canClick && scheduledBreak !== null ? 'cursor-pointer' : ''}`}
                                        title={
                                          canClick && scheduledBreak !== null
                                            ? `Click to copy to ${scheduledBreakIsPaid ? 'Paid' : 'Unpaid'} break (${scheduledBreakIsGlobal ? 'global policy' : 'shift override'})`
                                            : scheduledBreakIsGlobal
                                              ? 'Break from global policy (preview for this shift length)'
                                              : 'Break from shift override'
                                        }
                                      >
                                        {scheduledBreak ?? 0}
                                      </div>
                                      {row.shift && (
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                                          scheduledBreakIsPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                        }`}>
                                          {scheduledBreakIsPaid ? 'Paid' : 'Unpaid'}
                                        </span>
                                      )}
                                    </div>
                              </td>
                            </>
                          )}
                            </>
                          )}
                          {/* Actual - Empty for manual rows */}
                          {row.isManual ? (
                            <>
                              <td className="px-2 py-3 whitespace-nowrap pl-5 border-l-2 border-gray-300" colSpan={3}>
                                {/* Empty - Paid boxes are shown in Scheduled section for manual rows */}
                          </td>
                            </>
                          ) : (
                            <>
                              <td className="px-2 py-3 whitespace-nowrap pl-5 border-l-2 border-gray-300 relative">
                                <div 
                                  onClick={() => canClick && actualIn && handleCopyToPaid(actualIn, 'paidIn', 'actualIn')}
                                  className={`px-2 py-1 text-sm border rounded text-center ${
                                    actualInMatchesPaid && (!bothInMatchPaid || lastClickedIn !== 'scheduledIn')
                                      ? 'border-gray-300 bg-green-100 text-green-900'
                                      : 'border-gray-300 bg-gray-50 text-gray-900'
                                  } ${canClick && actualIn ? 'cursor-pointer' : ''}`}
                                  title={canClick && actualIn ? 'Click to copy to Paid In' : ''}
                                >
                                  {isOpenPunchRow ? 'Open ClockIn' : formatTime(actualIn)}
                                </div>
                                <div className="absolute top-1/2 left-[calc(100%-0.25rem)] w-3 h-0.5 border-t-2 border-dotted border-gray-400 transform -translate-y-1/2 z-10"></div>
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap">
                                <div 
                                  onClick={() => canClick && actualOut && handleCopyToPaid(actualOut, 'paidOut', 'actualOut')}
                                  className={`px-2 py-1 text-sm border rounded text-center ${
                                    actualOutMatchesPaid && (!bothOutMatchPaid || lastClickedOut !== 'scheduledOut')
                                      ? 'border-gray-300 bg-red-100 text-red-900'
                                      : 'border-gray-300 bg-gray-50 text-gray-900'
                                  } ${canClick && actualOut ? 'cursor-pointer' : ''}`}
                                  title={canClick && actualOut ? 'Click to copy to Paid Out' : ''}
                                >
                                  {actualOutDisplay}
                                </div>
                          </td>
                              <td className="px-2 py-3 whitespace-nowrap pr-5">
                                <div 
                                  onClick={() => canClick && actualBreak !== null && actualBreak !== undefined && handleCopyToPaid(actualBreak, 'paidBreakMinutes', 'actualBreak')}
                                  className={`px-2 py-1 text-sm border rounded text-center ${
                                    actualBreakMatchesPaid && (!bothBreakMatchPaid || lastClickedBreak !== 'scheduledBreak')
                                      ? 'border-gray-300 bg-yellow-100 text-yellow-900'
                                      : 'border-gray-300 bg-gray-50 text-gray-900'
                                  } ${canClick && actualBreak !== null && actualBreak !== undefined ? 'cursor-pointer' : ''}`}
                                  title={canClick && actualBreak !== null && actualBreak !== undefined ? 'Click to copy to Paid Break' : ''}
                                >
                                  {actualBreak !== null && actualBreak !== undefined ? actualBreak : ''}
                                </div>
                              </td>
                            </>
                          )}
                          {/* Paid */}
                          <td className="px-2 py-3 whitespace-nowrap pl-5 border-l-2 border-gray-300">
                            <input
                              key={`paid-in-${row.dateStr}-${row.shiftIndex}-${row.dayData?.id ?? 0}-${paidIn ?? ''}`}
                              type="time"
                              defaultValue={paidIn && paidIn !== '--:--' ? paidIn : ''}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (value) {
                                  handleDayUpdate(row.dateStr, row.shift?.id, { paidIn: value }, row.dayData?.id);
                                }
                              }}
                              // Manual rows should always allow Paid editing, even with no Actuals
                              disabled={disablePaidFields}
                              className={`w-24 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                paidIn && paidIn !== '--:--' ? 'border-gray-300 border-b-4 border-b-green-300' : 'border-gray-300'
                              } ${disablePaidFields ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                              placeholder="--:--"
                            />
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap">
                            <input
                              key={`paid-out-${row.dateStr}-${row.shiftIndex}-${row.dayData?.id ?? 0}-${paidOut ?? ''}`}
                              type="time"
                              defaultValue={paidOut && paidOut !== '--:--' ? paidOut : ''}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (value) {
                                  handleDayUpdate(row.dateStr, row.shift?.id, { paidOut: value }, row.dayData?.id);
                                }
                              }}
                              disabled={disablePaidFields}
                              className={`w-24 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                paidOut && paidOut !== '--:--' ? 'border-gray-300 border-b-4 border-b-red-300' : 'border-gray-300'
                              } ${disablePaidFields ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                              placeholder="--:--"
                            />
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap">
                            <input
                              key={`paid-break-${row.dateStr}-${row.shiftIndex}-${row.dayData?.id ?? 0}-${row.dayData?.paidBreakMinutes ?? 0}`}
                              type="number"
                              min="0"
                              value={row.dayData?.paidBreakMinutes ?? ''}
                              onChange={(e) => {
                                handleDayUpdate(row.dateStr, row.shift?.id, { paidBreakMinutes: parseInt(e.target.value) || 0 }, row.dayData?.id);
                              }}
                              disabled={disablePaidFields}
                              title="Paid break (mins)"
                              className={`w-16 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                row.dayData?.paidBreakMinutes != null ? 'border-gray-300 border-b-4 border-b-green-300' : 'border-gray-300'
                              } ${disablePaidFields ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                            />
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap">
                            <input
                              key={`unpaid-break-${row.dateStr}-${row.shiftIndex}-${row.dayData?.id ?? 0}-${row.dayData?.unpaidBreakMinutes ?? 0}`}
                              type="number"
                              min="0"
                              value={row.dayData?.unpaidBreakMinutes ?? ''}
                              onChange={(e) => {
                                handleDayUpdate(row.dateStr, row.shift?.id, { unpaidBreakMinutes: parseInt(e.target.value) || 0 }, row.dayData?.id);
                              }}
                              disabled={disablePaidFields}
                              title="Unpaid break (mins) — deducted from total"
                              className={`w-16 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                row.dayData?.unpaidBreakMinutes != null ? 'border-gray-300 border-b-4 border-b-red-300' : 'border-gray-300'
                              } ${disablePaidFields ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                            />
                          </td>
                          {/* Schedule */}
                          <td className="px-4 py-3 whitespace-nowrap pl-5 border-l-2 border-gray-300">
                            {(() => {
                              // Get default schedule code from shift if dayData doesn't have one
                              const dayScheduleId = row.dayData?.scheduleId ?? undefined;
                              const scheduleById =
                                dayScheduleId != null ? schedules.find((s) => s.id === dayScheduleId) : undefined;
                              const defaultScheduleCode = scheduleById
                                ? getTwoLetterCode(scheduleById.name)
                                : row.dayData?.scheduleCode ||
                                  (row.shift?.schedule ? getTwoLetterCode(row.shift.schedule.name) : '');
                              const selectedSchedule =
                                scheduleById ||
                                availableSchedules.find((s) => getTwoLetterCode(s.name) === defaultScheduleCode) ||
                                (row.shift?.schedule ? schedules.find((s) => s.id === row.shift!.schedule!.id) : null);
                              const scheduleTooltip = selectedSchedule ? selectedSchedule.name : 
                                (row.shift?.schedule ? row.shift.schedule.name : 
                                (defaultScheduleCode ? `Schedule: ${defaultScheduleCode}` : ''));
                              const rowKey = `${row.dateStr}-${row.shiftIndex}`;
                              const isOpen = openScheduleDropdown === rowKey;

                              return (
                                <div className="relative">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenScheduleDropdown(isOpen ? null : rowKey);
                                    }}
                                    className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer bg-white text-center hover:bg-gray-50"
                                    title={scheduleTooltip}
                                  >
                                    {defaultScheduleCode || ''}
                                  </button>
                                  {isOpen && (
                                    <div className="absolute z-50 mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                                      <div
                                        className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDayUpdate(row.dateStr, row.shift?.id, { scheduleId: 0 }, row.dayData?.id);
                                          setOpenScheduleDropdown(null);
                                        }}
                                      >
                                        <div className="font-semibold text-gray-900">Clear</div>
                                        <div className="text-xs text-gray-500">Remove schedule</div>
                                      </div>
                                      {availableSchedules.map(schedule => {
                                        const code = getTwoLetterCode(schedule.name);
                                        const isSelected =
                                          schedule.id === dayScheduleId ||
                                          (dayScheduleId == null && code === defaultScheduleCode);
                                        return (
                                          <div
                                            key={schedule.id}
                                            className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${
                                              isSelected ? 'bg-blue-50' : ''
                                            }`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDayUpdate(
                                                row.dateStr,
                                                row.shift?.id,
                                                { scheduleId: schedule.id, scheduleCode: code },
                                                row.dayData?.id
                                              );
                                              setOpenScheduleDropdown(null);
                                            }}
                                          >
                                            <div className="font-semibold text-gray-900">{code}</div>
                                            <div className="text-xs text-gray-500">{schedule.name}</div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          {/* Position */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            {(() => {
                              // Get default position code from shift if dayData doesn't have one
                              const dayPositionId = row.dayData?.positionId ?? undefined;
                              const positionById =
                                dayPositionId != null ? positions.find((p) => p.id === dayPositionId) : undefined;
                              const defaultPositionCode = positionById
                                ? getTwoLetterCode(positionById.title)
                                : row.dayData?.positionCode ||
                                  (row.shift?.position ? getTwoLetterCode(row.shift.position.title) : '');
                              const selectedPosition =
                                positionById ||
                                availablePositions.find((p) => getTwoLetterCode(p.title) === defaultPositionCode) ||
                                (row.shift?.position ? positions.find((p) => p.id === row.shift!.position!.id) : null);
                              const positionTooltip = selectedPosition ? selectedPosition.title : 
                                (row.shift?.position ? row.shift.position.title : 
                                (defaultPositionCode ? `Position: ${defaultPositionCode}` : ''));
                              const rowKey = `${row.dateStr}-${row.shiftIndex}`;
                              const isOpen = openPositionDropdown === rowKey;

                              return (
                                <div className="relative">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenPositionDropdown(isOpen ? null : rowKey);
                                    }}
                                    className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer bg-white text-center hover:bg-gray-50"
                                    title={positionTooltip}
                                  >
                                    {defaultPositionCode || ''}
                                  </button>
                                  {isOpen && (
                                    <div className="absolute z-50 mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                                      <div
                                        className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDayUpdate(row.dateStr, row.shift?.id, { positionId: 0 }, row.dayData?.id);
                                          setOpenPositionDropdown(null);
                                        }}
                                      >
                                        <div className="font-semibold text-gray-900">Clear</div>
                                        <div className="text-xs text-gray-500">Remove position</div>
                                      </div>
                                      {availablePositions.map(position => {
                                        const code = getTwoLetterCode(position.title);
                                        const isSelected =
                                          position.id === dayPositionId ||
                                          (dayPositionId == null && code === defaultPositionCode);
                                        return (
                                          <div
                                            key={position.id}
                                            className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${
                                              isSelected ? 'bg-blue-50' : ''
                                            }`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDayUpdate(
                                                row.dateStr,
                                                row.shift?.id,
                                                { positionId: position.id, positionCode: code },
                                                row.dayData?.id
                                              );
                                              setOpenPositionDropdown(null);
                                            }}
                                          >
                                            <div className="font-semibold text-gray-900">{code}</div>
                                            <div className="text-xs text-gray-500">{position.title}</div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          {/* Pay Extra (§4.4) — only when employee AllowAdditiveHourlyPay */}
                          {employees.find((e) => e.id === selectedEmployeeId)?.payroll?.allowAdditiveHourlyPay === true && (
                          <td className="px-4 py-3 whitespace-nowrap text-center">
                            <input
                              type="checkbox"
                              checked={row.dayData?.isPayExtra === true}
                              disabled={!!timecard?.isApproved || isSelectedLocationInactive || !row.dayData}
                              title="Pay Extra — additive matrix pay on top of salary"
                              onChange={(e) => {
                                const next = e.target.checked;
                                const emp = employees.find((x) => x.id === selectedEmployeeId);
                                const dayPos = row.dayData?.positionId ?? row.shift?.positionId;
                                if (
                                  next &&
                                  dayPos != null &&
                                  emp?.profile?.mainPositionId != null &&
                                  dayPos === emp.profile.mainPositionId
                                ) {
                                  const ok = window.confirm(
                                    'Pay Extra is on with the employee’s main position. Additive matrix pay will apply on top of salary for these hours. Continue?'
                                  );
                                  if (!ok) return;
                                }
                                handleDayUpdate(
                                  row.dateStr,
                                  row.shift?.id,
                                  { isPayExtra: next },
                                  row.dayData?.id
                                );
                              }}
                              className="h-4 w-4 rounded border-neutral-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                            />
                          </td>
                          )}
                          {/* Extras */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={row.dayData?.extras || 0}
                              onChange={(e) => {
                                handleDayUpdate(row.dateStr, row.shift?.id, { extras: parseFloat(e.target.value) || 0 });
                              }}
                              disabled={!!timecard?.isApproved || isSelectedLocationInactive}
                              className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-orange-600 font-medium disabled:bg-gray-100 disabled:text-gray-500"
                            />
                          </td>
                          {/* Total Hours */}
                          <td className="px-4 py-3 whitespace-nowrap relative">
                            <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${row.dayData?.syncErrorMessage ? 'text-red-600' : 'text-blue-600'}`}>
                              {(row.dayData?.totalHours || 0).toFixed(2)}
                            </span>
                            {row.dayData?.syncErrorMessage && (
                              <span className="text-xs text-red-600 font-medium" title={row.dayData.syncErrorMessage}>
                                {row.dayData.syncErrorMessage}
                              </span>
                            )}
                            {row.dayData && savingDays.has(row.dayData.id) && (
                                <Clock className="w-4 h-4 text-gray-400 animate-spin" />
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const rowKey = `${row.dateStr}-${row.shiftIndex}`;
                                  setOpenMenuRow(openMenuRow === rowKey ? null : rowKey);
                                }}
                                className="p-1 hover:bg-gray-100 rounded transition-colors"
                                title="Options"
                              >
                                <MoreVertical className="w-4 h-4 text-gray-500" />
                              </button>
                              {openMenuRow === `${row.dateStr}-${row.shiftIndex}` && (
                                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 min-w-[150px]">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddManualTimecard(row.dateStr);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                  >
                                    Add Timecard
                                  </button>
                                  {row.isManual && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDeleteConfirm({
                                          dateStr: row.dateStr,
                                          shiftIndex: row.shiftIndex,
                                          dayDataId: row.dayData?.id,
                                          dateLabel: `${format(row.date, 'EEEE')} ${formatCompanyDate(row.date)}`,
                                        });
                                        setOpenMenuRow(null);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                      Delete Timecard
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="bg-gray-50 border-t border-gray-200">
                      <td colSpan={12} className="px-4 py-2 text-sm text-right font-medium text-gray-700">
                        Extras total ({companyCurrency}):
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm font-mono font-semibold text-orange-700">
                        {extrasTotalForWeek.toFixed(2)}
                      </td>
                      <td className="px-4 py-2" aria-hidden />
                    </tr>
                    {weeklyPremiumHours.sundayPremiumHours > 0 && (
                      <tr className="bg-gray-50">
                        <td colSpan={13} className="px-4 py-2 text-sm text-right font-medium text-gray-700">
                          Sunday premium hours:
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-800">
                          {weeklyPremiumHours.sundayPremiumHours.toFixed(2)}
                        </td>
                      </tr>
                    )}
                    {weeklyPremiumHours.bhPremiumHours > 0 && (
                      <tr className="bg-gray-50">
                        <td colSpan={13} className="px-4 py-2 text-sm text-right font-medium text-gray-700">
                          BH premium hours:
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-800">
                          {weeklyPremiumHours.bhPremiumHours.toFixed(2)}
                        </td>
                      </tr>
                    )}
                    {/* Location totals - selected first (includes leave), then others */}
                    {otherLocationTotals.map(({ locationId, locationName, totalHours, isSelected }) => {
                      const displayHours = isSelected ? totalHours + leaveTotalHours : totalHours;
                      return (
                        <tr key={locationId} className={isSelected ? 'bg-gray-50 font-semibold' : 'bg-gray-50/80'}>
                          <td colSpan={13} className={`px-4 py-2 text-sm ${isSelected ? 'text-gray-900' : 'text-gray-600 pl-8'}`}>
                            Total hours for {locationName}{isSelected && leaveTotalHours > 0 ? ' (incl. leave)' : ''}:
                          </td>
                          <td className={`px-4 py-2 whitespace-nowrap text-sm font-medium ${isSelected ? 'text-blue-600' : 'text-gray-600'}`}>
                            {displayHours.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Grand total when multiple locations */}
                    {otherLocationTotals.length > 1 && (
                      <tr className="bg-blue-50 font-semibold border-t-2 border-blue-200">
                        <td colSpan={13} className="px-4 py-3 text-sm text-blue-900">
                          Total (all locations + leave):
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-700">
                          {grandTotalHours.toFixed(2)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Sync Step 1 Modal - Show Shifts Report */}
      {showSyncStep1Modal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-50 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Sync Step 1: Shifts Report</h2>
              <button
                onClick={async () => {
                  setShowSyncStep1Modal(false);
                  // Refetch timecard data after closing modal to reflect DB changes
                  await refetchTimecardData();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Found {syncStep1Data.length} shift(s), {syncStep2Data.length} attendance record(s), {syncStep3Data.length} processed record(s), {syncStep4Data.length} matched record(s), {syncStep5Data.length} unmatched record(s), {syncStep7Data.length} date record(s), {syncStep8Data.length} comparison record(s), {syncStep9Data.length} paid calculation record(s), and {syncStep10Data.length} final status record(s) for the selected employee and week.
              </p>
            </div>

            {/* Step 1: Shifts */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Step 1: Shifts</h3>
              {syncStep1Data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">In Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Out Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Break Paid</th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-50 divide-y divide-gray-200">
                      {syncStep1Data.map((shift, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{shift.shiftId}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{shift.date}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{shift.inTime}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{shift.outTime}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {shift.breakUseGlobalSettings
                              ? (calculateDefaultBreakMinutes(shift.inTime, shift.outTime, breakTimeSettings?.rules) ?? 0)
                              : (shift.breakMins ?? 0)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              shift.breakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {shift.breakPaid ? 'Paid' : 'Unpaid'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 border rounded">
                  No shifts found for this employee and week.
                </div>
              )}
            </div>

            {/* Step 2: Attendance Records */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Step 2: Actual Times (Attendance Records)</h3>
              {syncStep2Data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attendance ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">In Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Out Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adjusted In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adjusted Out</th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-50 divide-y divide-gray-200">
                      {syncStep2Data.map((attendance, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{attendance.attendanceId}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{attendance.date}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{attendance.inTime}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{attendance.outTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{attendance.adjustedInTime || '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{attendance.adjustedOutTime || '--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 border rounded">
                  No attendance records found for this employee and week.
                </div>
              )}
            </div>

            {/* Step 3: Processed Records (Adjusted times merged into In/Out times) */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Step 3: Processed Records (Adjusted times merged)</h3>
              {syncStep3Data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attendance ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">In Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Out Time</th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-50 divide-y divide-gray-200">
                      {syncStep3Data.map((record, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.attendanceId}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.date}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.inTime}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.outTime || '--:--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 border rounded">
                  No processed records found.
                </div>
              )}
            </div>

            {/* Step 4: Matched Records (Step 1 shifts with overlapping Step 3 attendance) */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Step 4: Matched Records (Shifts with Overlapping Attendance)</h3>
              {syncStep4Data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break Paid</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual In Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Out Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Matched Count</th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-50 divide-y divide-gray-200">
                      {syncStep4Data.map((record, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftId}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftDate}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftInTime}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftOutTime}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftBreakMins}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              record.shiftBreakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {record.shiftBreakPaid ? 'Paid' : 'Unpaid'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualInTime ? record.actualInDate : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualOutTime ? record.actualOutDate : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualBreakMins !== null ? record.actualBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.matchedAttendanceCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 border rounded">
                  No matched records found.
                </div>
              )}
            </div>

            {/* Step 5: Unmatched Records (Step 3 records without matching Step 1 shifts) */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Step 5: Unmatched Records (Attendance without Matching Shifts)</h3>
              {syncStep5Data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break Paid</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual In Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Out Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Matched Count</th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-50 divide-y divide-gray-200">
                      {syncStep5Data.map((record, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftId ?? '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftDate ?? '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftInTime ?? '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftOutTime ?? '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftBreakMins ?? '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {record.shiftBreakPaid !== null ? (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                record.shiftBreakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {record.shiftBreakPaid ? 'Paid' : 'Unpaid'}
                              </span>
                            ) : (
                              '--'
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualInTime ? record.actualInDate : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualOutTime ? record.actualOutDate : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualBreakMins !== null ? record.actualBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.matchedAttendanceCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 border rounded">
                  No unmatched records found.
                </div>
              )}
            </div>

            {/* Step 7: Date-based Records with Hash */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Step 7: Date-based Records with Hash</h3>
              {syncStep7Data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break Paid</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hash</th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-50 divide-y divide-gray-200">
                      {syncStep7Data.map((record, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.date}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftBreakMins !== null ? record.shiftBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {record.shiftBreakPaid !== null ? (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                record.shiftBreakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {record.shiftBreakPaid ? 'Paid' : 'Unpaid'}
                              </span>
                            ) : (
                              '--'
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualBreakMins !== null ? record.actualBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-xs break-all max-w-xs">{record.hash}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 border rounded">
                  No date records found.
                </div>
              )}
            </div>

            {/* Step 8: Comparison Records with MatchType */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Step 8: Comparison Records with MatchType</h3>
              {syncStep8Data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break Paid</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hash</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">MatchType</th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-50 divide-y divide-gray-200">
                      {syncStep8Data.map((record, index) => (
                        <tr key={index} className={`hover:bg-gray-50 ${
                          record.matchType === 'Match' ? 'bg-green-50' :
                          record.matchType === 'NO_DB_Hash' ? 'bg-yellow-50' :
                          record.matchType === 'NO_NEW_Hash' ? 'bg-red-50' : ''
                        }`}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.date}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftBreakMins !== null ? record.shiftBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {record.shiftBreakPaid !== null ? (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                record.shiftBreakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {record.shiftBreakPaid ? 'Paid' : 'Unpaid'}
                              </span>
                            ) : (
                              '--'
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualBreakMins !== null ? record.actualBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-xs break-all max-w-xs">{record.hash}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              record.matchType === 'Match' ? 'bg-green-100 text-green-800' :
                              record.matchType === 'NO_DB_Hash' ? 'bg-yellow-100 text-yellow-800' :
                              record.matchType === 'NO_NEW_Hash' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {record.matchType}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 border rounded">
                  No comparison records found.
                </div>
              )}
            </div>

            {/* Step 9: Paid Calculation Records */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Step 9: Paid Calculation Records</h3>
              {syncStep9Data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break Paid</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hash</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">MatchType</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid Break Paid</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {syncStep9Data.map((record, index) => (
                        <tr key={index} className={`hover:bg-gray-50 ${
                          record.matchType === 'Match' ? 'bg-green-50' :
                          record.matchType === 'NO_DB_Hash' ? 'bg-yellow-50' :
                          record.matchType === 'NO_NEW_Hash' ? 'bg-red-50' : ''
                        }`}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.date}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftBreakMins !== null ? record.shiftBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {record.shiftBreakPaid !== null ? (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                record.shiftBreakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {record.shiftBreakPaid ? 'Paid' : 'Unpaid'}
                              </span>
                            ) : (
                              '--'
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualBreakMins !== null ? record.actualBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-xs break-all max-w-xs">{record.hash}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              record.matchType === 'Match' ? 'bg-green-100 text-green-800' :
                              record.matchType === 'NO_DB_Hash' ? 'bg-yellow-100 text-yellow-800' :
                              record.matchType === 'NO_NEW_Hash' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {record.matchType}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-blue-600">{record.paidInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-blue-600">{record.paidOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-blue-600">{record.paidBreakMins !== null ? record.paidBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {record.paidBreakPaid !== null ? (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                record.paidBreakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {record.paidBreakPaid ? 'Paid' : 'Unpaid'}
                              </span>
                            ) : (
                              '--'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 border rounded">
                  No paid calculation records found.
                </div>
              )}
            </div>

            {/* Step 10: Final Status Records (Inserted/Deleted/Not Touched) */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Step 10: Final Status Records</h3>
              {syncStep10Data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break Paid</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hash</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">MatchType</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid Break Paid</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Hours</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {syncStep10Data
                        .slice()
                        .sort((a, b) => {
                          // First sort by date
                          const dateA = a.date || '';
                          const dateB = b.date || '';
                          if (dateA !== dateB) {
                            return dateA.localeCompare(dateB);
                          }
                          
                          // Within the same date, sort by ActualIn time if available, otherwise by ShiftIn time
                          const getSortTime = (record: typeof a): string => {
                            // If ActualIn exists, use it for sorting
                            if (record.actualInTime && record.actualInTime !== '--:--') {
                              return `${record.date}T${record.actualInTime}:00`;
                            }
                            // Otherwise use ShiftIn time
                            if (record.shiftInTime && record.shiftInTime !== '--:--') {
                              return `${record.date}T${record.shiftInTime}:00`;
                            }
                            // Fallback: use date only (will sort these last)
                            return `${record.date}T00:00:00`;
                          };
                          
                          const timeA = getSortTime(a);
                          const timeB = getSortTime(b);
                          return timeA.localeCompare(timeB);
                        })
                        .map((record, index) => (
                        <tr key={index} className={`hover:bg-gray-50 ${
                          record.matchType === 'Match' ? 'bg-green-50' :
                          record.matchType === 'NO_DB_Hash' ? 'bg-yellow-50' :
                          record.matchType === 'NO_NEW_Hash' ? 'bg-red-50' : ''
                        }`}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.date}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftBreakMins !== null ? record.shiftBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {record.shiftBreakPaid !== null ? (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                record.shiftBreakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {record.shiftBreakPaid ? 'Paid' : 'Unpaid'}
                              </span>
                            ) : (
                              '--'
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualBreakMins !== null ? record.actualBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-xs break-all max-w-xs">{record.hash}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              record.matchType === 'Match' ? 'bg-green-100 text-green-800' :
                              record.matchType === 'NO_DB_Hash' ? 'bg-yellow-100 text-yellow-800' :
                              record.matchType === 'NO_NEW_Hash' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {record.matchType}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-blue-600">{record.paidInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-blue-600">{record.paidOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-blue-600">{record.paidBreakMins !== null ? record.paidBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {record.paidBreakPaid !== null ? (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                record.paidBreakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {record.paidBreakPaid ? 'Paid' : 'Unpaid'}
                              </span>
                            ) : (
                              '--'
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-blue-600">
                            {record.totalHours !== null ? record.totalHours.toFixed(2) : '--'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-medium font-semibold ${
                              record.status === 'Inserted' ? 'bg-green-100 text-green-800' :
                              record.status === 'Deleted' ? 'bg-red-100 text-red-800' :
                              record.status === 'Not Touched' ? 'bg-gray-100 text-gray-800' :
                              record.status?.includes('Failed') ? 'bg-orange-100 text-orange-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {record.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 border rounded">
                  No final status records found.
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={async () => {
                  setShowSyncStep1Modal(false);
                  // Refetch timecard data after closing modal to reflect DB changes
                  await refetchTimecardData();
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Timecards;
